import { 
  getListaByIdLocal, closeListaLocal, saveListaLocal, deleteListaLocal,
  saveListaItemLocal, deleteListaItemLocal, toggleListaItemLocal, saveListaMensagemLocal,
  searchHistoricalProducts, cleanProductDescription, optimizeShoppingListBackend, getMultiStoreQuotesForList
} from '../db.js';
import { escapeHTML } from '../security.js';
import { authManager, getSavedScope, setSavedScope } from '../auth-manager.js';
import { getCategoryIcon, categorizeItem } from '../categorizer.js';
import { unlinkNotaFromShoppingList } from '../utils/reconciliation.js';

function formatStoreShortName(name, maxWords = 2) {
  if (!name) return '';
  const trimmed = String(name).trim();
  const cleaned = trimmed.replace(/\b(ltda|s\/?a|s\.a|eireli|me|epp)\b\.?/gi, '').trim();
  const words = (cleaned || trimmed).split(/\s+/).filter(Boolean);
  if (words.length <= maxWords) return cleaned || trimmed;
  return words.slice(0, maxWords).join(' ') + '...';
}

export async function renderListaDetailView(container, id, syncManager) {
  if (!id) {
    window.location.hash = '#listas';
    return;
  }

  let lista = null;
  let multiStoreQuotes = null;
  let selectedStoreFilter = sessionStorage.getItem('active_list_store_filter') || 'all';
  let isChatCollapsed = true;
  let chatHeight = 340;
  let quotedItem = null;
  let replyToMessage = null;
  let currentSearchQuery = '';
  let lastLocalMutationTime = 0;
  const expandedComprados = new Set();
  let bindItemRowEvents = null;
  let openBuyModal = null;
  let openDeleteItemModal = null;
  let lastChatStateKey = null;

  // 1. Background cloud sync
  async function syncCloudData() {
    if (!authManager.isAuthenticated()) return;
    try {
      const res = await authManager.fetchWithAuth(`/api/listas/${id}`);
      if (res.ok) {
        const cloudLista = await res.json();
        if (cloudLista && window.location.hash.startsWith(`#lista/${id}`)) {
          // If the user made a local mutation in the last 3.5 seconds, don't overwrite items
          const isRecentUserAction = (Date.now() - lastLocalMutationTime) < 3500;
          if (!isRecentUserAction || !lista) {
            lista = cloudLista;
            if (lista && Array.isArray(lista.items)) {
              multiStoreQuotes = await getMultiStoreQuotesForList(lista.items);
            }
            render();
          }
        }
      } else if (!lista) {
        if (window.location.hash.startsWith(`#lista/${id}`)) {
          container.innerHTML = `
            <div class="chart-card text-center" style="padding: 2.5rem 1.5rem; margin-top: 1.5rem;">
              <span class="material-symbols-outlined" style="font-size: 3.5rem; color: #94A3B8;">checklist</span>
              <h3 style="font-size: 1.2rem; font-weight: 700; color: #111827; margin: 0.5rem 0;">Lista não encontrada</h3>
              <p class="text-muted" style="font-size: 0.875rem; margin-bottom: 1.5rem;">A lista de compras solicitada não existe ou foi removida.</p>
              <a href="#listas" class="btn btn-primary" style="border-radius: var(--radius-pill); font-weight: 700; display: inline-flex; align-items: center; gap: 0.4rem; padding: 0.6rem 1.2rem;">
                <span class="material-symbols-outlined">arrow_back</span> Voltar para Listas
              </a>
            </div>
          `;
        }
      }
    } catch (e) {
      console.warn('[renderListaDetailView] Background sync failed:', e);
      if (!lista && window.location.hash.startsWith(`#lista/${id}`)) {
        container.innerHTML = `
          <div class="chart-card text-center" style="padding: 2.5rem 1.5rem; margin-top: 1.5rem;">
            <span class="material-symbols-outlined" style="font-size: 3.5rem; color: #94A3B8;">checklist</span>
            <h3 style="font-size: 1.2rem; font-weight: 700; color: #111827; margin: 0.5rem 0;">Lista não encontrada</h3>
            <p class="text-muted" style="font-size: 0.875rem; margin-bottom: 1.5rem;">Não foi possível carregar a lista solicitada.</p>
            <a href="#listas" class="btn btn-primary" style="border-radius: var(--radius-pill); font-weight: 700; display: inline-flex; align-items: center; gap: 0.4rem; padding: 0.6rem 1.2rem;">
              <span class="material-symbols-outlined">arrow_back</span> Voltar para Listas
            </a>
          </div>
        `;
      }
    }
  }

  // 2. Cache-first load data
  async function loadData(forceSync = false) {
    lista = await getListaByIdLocal(Number(id)).catch(() => null);
    if (lista && Array.isArray(lista.items)) {
      try {
        multiStoreQuotes = await getMultiStoreQuotesForList(lista.items);
      } catch (e) {}
    }

    if (lista && !forceSync) {
      render();
      syncCloudData(); // background non-blocking sync
    } else {
      await syncCloudData();
    }
  }

  await loadData(false);
  if (window.location.hash !== `#lista/${id}`) return;

  function render(forceFull = false) {
    if (window.location.hash !== `#lista/${id}`) return;
    const listTitle = lista.name || lista.nome || 'Lista de Compras';
    window.dispatchEvent(new CustomEvent('nfce-page-title-changed', { detail: { title: listTitle } }));

    const isFamilyList = (lista.scope === 'family' || lista.escopo === 'family');

    // The in-place refresh only touches the items area, so any chat state change needs a full render
    const chatStateKey = [
      isChatCollapsed, chatHeight, quotedItem?.id ?? '', replyToMessage?.id ?? '', (lista.messages || []).length
    ].join('|');
    const chatStateChanged = chatStateKey !== lastChatStateKey;

    // If already mounted and not requesting full re-render, update list area and summary in-place
    if (!forceFull && !chatStateChanged && container.querySelector('#lista-content-wrapper') && container.querySelector('#items-list-section')) {
      refreshListArea();
      return;
    }
    lastChatStateKey = chatStateKey;

    function computeListMetricsAndGrouping() {
      const currentItems = lista.items || [];
      const currentCheckedCount = currentItems.filter(i => Boolean(i.isChecked || i.is_checked)).length;
      const currentProgressPct = currentItems.length > 0 ? Math.round((currentCheckedCount / currentItems.length) * 100) : 0;

      const currentEstVal = currentItems.reduce((sum, it) => {
        const q = it.quantity || it.quantidade || 1;
        const p = it.estimatedPrice || it.precoEstimado || 0;
        return sum + (q * p);
      }, 0);

      const currentActVal = currentItems.reduce((sum, it) => {
        const isChecked = Boolean(it.isChecked || it.is_checked);
        if (!isChecked) return sum;
        const q = it.quantity || it.quantidade || 1;
        const actQ = (it.actualQuantity || it.quantidadeComprada || 0) > 0 ? (it.actualQuantity || it.quantidadeComprada) : q;
        const actP = (it.actualPrice || it.precoReal || 0) > 0 ? (it.actualPrice || it.precoReal) : (it.estimatedPrice || it.precoEstimado || 0);
        return sum + (actQ * actP);
      }, 0);

      const currentDiffVal = currentActVal - currentEstVal;
      const currentIsOverBudget = currentDiffVal > 0;

      const currentItemBestStoreMap = {};
      const currentStoreSummaryMap = {};
      if (multiStoreQuotes && Array.isArray(multiStoreQuotes.itemsMatrix)) {
        multiStoreQuotes.itemsMatrix.forEach(row => {
          const itId = row.item?.id;
          if (itId && row.bestStore) {
            currentItemBestStoreMap[itId] = {
              bestStore: row.bestStore,
              lowestPrice: row.lowestPrice
            };
            if (!currentStoreSummaryMap[row.bestStore]) {
              currentStoreSummaryMap[row.bestStore] = { count: 0, total: 0 };
            }
            currentStoreSummaryMap[row.bestStore].count += 1;
            currentStoreSummaryMap[row.bestStore].total += (row.lowestPrice * row.quantity);
          }
        });
      }

      const currentGroupedItems = {};
      currentItems.forEach(it => {
        if (selectedStoreFilter !== 'all') {
          const bestStore = currentItemBestStoreMap[it.id]?.bestStore;
          if (bestStore !== selectedStoreFilter) return;
        }

        let cat = it.categoryName || it.categoriaNome;
        if (!cat || cat === 'Outros') {
          const detected = categorizeItem(it.description || it.descricao || '');
          if (detected?.nome && detected.nome !== 'Outros') {
            cat = detected.nome;
          } else {
            cat = 'Outros';
          }
        }
        if (!currentGroupedItems[cat]) currentGroupedItems[cat] = [];
        currentGroupedItems[cat].push({ ...it, displayCategory: cat });
      });

      return {
        items: currentItems,
        checkedCount: currentCheckedCount,
        progressPct: currentProgressPct,
        estVal: currentEstVal,
        actVal: currentActVal,
        diffVal: currentDiffVal,
        isOverBudget: currentIsOverBudget,
        itemBestStoreMap: currentItemBestStoreMap,
        storeSummaryMap: currentStoreSummaryMap,
        groupedItems: currentGroupedItems,
      };
    }

    function renderItemsAreaHTML(currentItems, currentGroupedItems, currentItemBestStoreMap, currentIsClosed, currentIsFamilyList) {
      if (currentItems.length === 0) {
        return `
          <div class="chart-card text-center text-muted" style="padding: 2.5rem 1.5rem; font-size: 0.875rem; border-radius: 1rem;">
            <span class="material-symbols-outlined" style="font-size: 36px; opacity: 0.4; display: block; margin-bottom: 0.5rem;">playlist_add</span>
            Nenhum produto adicionado nesta lista ainda.
          </div>
        `;
      }

      return `
        <!-- No Results State on Search Filter -->
        <div id="search-no-results-message" class="chart-card text-center text-muted" style="display: none; padding: 2.5rem 1.5rem; font-size: 0.875rem; border-radius: 1rem; margin-bottom: 1rem;">
          <span class="material-symbols-outlined" style="font-size: 36px; opacity: 0.4; display: block; margin-bottom: 0.5rem;">search_off</span>
          Nenhum produto encontrado para "<strong id="search-no-results-query"></strong>".
        </div>

        <div id="items-list-container" class="chart-card" style="padding: 0; overflow: hidden; border-radius: 1rem; border: 1px solid var(--border-color); background: var(--bg-surface); box-shadow: var(--shadow-card);">
          ${Object.entries(currentGroupedItems).map(([catName, catItems], catIdx) => {
            const icon = getCategoryIcon(catName);
            const pendingItems = catItems.filter(it => !it.isChecked && !it.is_checked);
            const completedItems = catItems.filter(it => Boolean(it.isChecked || it.is_checked));
            const isAccordionOpen = expandedComprados.has(catName);
            const isFirstCategory = catIdx === 0;

            const catTotal = catItems.reduce((sum, it) => {
              const isChecked = Boolean(it.isChecked || it.is_checked);
              const est = it.estimatedPrice || it.precoEstimado || 0;
              const qty = it.quantity || it.quantidade || 1;
              const actQty = it.actualQuantity || it.quantidadeComprada || 0;
              const actPrice = it.actualPrice || it.precoReal || 0;
              const effQty = isChecked ? (actQty > 0 ? actQty : qty) : qty;
              const effPrice = isChecked && actPrice > 0 ? actPrice : est;
              return sum + (effPrice * effQty);
            }, 0);

            const completedTotal = completedItems.reduce((sum, it) => {
              const est = it.estimatedPrice || it.precoEstimado || 0;
              const qty = it.quantity || it.quantidade || 1;
              const actQty = it.actualQuantity || it.quantidadeComprada || 0;
              const actPrice = it.actualPrice || it.precoReal || 0;
              const effQty = actQty > 0 ? actQty : qty;
              const effPrice = actPrice > 0 ? actPrice : est;
              return sum + (effPrice * effQty);
            }, 0);

            const renderItemRow = (it, isInsideComprados = false) => {
              const isChecked = Boolean(it.isChecked || it.is_checked);
              const est = it.estimatedPrice || it.precoEstimado || 0;
              const qty = it.quantity || it.quantidade || 1;
              const actQty = it.actualQuantity || it.quantidadeComprada || 0;
              const actPrice = it.actualPrice || it.precoReal || 0;
              const unit = it.unit || it.unidade || 'UN';
              const rawDesc = it.description || it.descricao || 'Item';
              const itemDesc = cleanProductDescription(rawDesc) || rawDesc;
              
              const isPartial = isChecked && actQty > 0 && actQty < qty;
              const effectiveQty = isChecked ? (actQty > 0 ? actQty : qty) : qty;
              const effectivePrice = isChecked && actPrice > 0 ? actPrice : est;
              const rowTotal = (effectivePrice * effectiveQty).toFixed(2).replace('.', ',');
              const bestQuoteInfo = currentItemBestStoreMap[it.id];

              const rowBg = isInsideComprados ? 'var(--bg-category-header, #F5F4EE)' : 'var(--bg-surface)';
              const chipBg = (isPartial && !isInsideComprados) ? 'var(--color-warning-bg)' : (isInsideComprados ? 'var(--bg-surface)' : 'var(--bg-surface-dim)');
              const chipBorder = (isPartial && !isInsideComprados) ? 'var(--color-warning-border)' : 'var(--border-color)';
              const chipColor = (isPartial && !isInsideComprados) ? 'var(--color-warning-text)' : (isInsideComprados ? 'var(--text-muted)' : 'var(--text-main)');
              const priceColor = (isPartial && !isInsideComprados) ? '#D97706' : (isInsideComprados ? 'var(--text-muted)' : (isChecked ? 'var(--text-muted)' : 'var(--text-main)'));

              return `
                <div class="swipe-item-wrapper" data-item-id="${it.id}" data-item-name="${escapeHTML(itemDesc.toLowerCase())}" data-barcode="${escapeHTML(it.barcode || '')}" style="${isInsideComprados ? 'background: var(--bg-category-header, #F5F4EE);' : ''}">
                  <!-- Swipe Underlays (Only for pending items) -->
                  ${!isInsideComprados ? `
                    <div class="swipe-action-bg swipe-action-left" style="opacity: 0;">
                      <span class="material-symbols-outlined" style="font-size: 18px;">${isChecked ? 'undo' : 'check_circle'}</span>
                      <span>${isChecked ? 'Desmarcar' : 'Comprar Tudo'}</span>
                    </div>
                    <div class="swipe-action-bg swipe-action-right" style="opacity: 0;">
                      <span>Ajustar Qtd</span>
                      <span class="material-symbols-outlined" style="font-size: 18px;">tune</span>
                    </div>
                  ` : ''}

                  <!-- Compact Item Row with 2 lines: Line 1 (Check + Qty + Title ellipsis) & Line 2 (Best Quote/Chat on left, Price on right) -->
                  <div class="lista-item-row-compact ${isChecked ? 'is-checked' : ''} ${isPartial && !isInsideComprados ? 'is-partial' : ''}" data-item-id="${it.id}" style="border-bottom: 1px solid var(--border-color-subtle, rgba(0, 0, 0, 0.06)); background: ${rowBg}; position: relative; z-index: 2; ${isInsideComprados ? 'opacity: 1 !important;' : ''}">
                    
                    <!-- Line 1: Checkbox + Quantity Chip + Product Title to edge with ellipsis -->
                    <div class="lista-item-line-1" style="display: flex; align-items: center; gap: 0.5rem; width: 100%; min-width: 0;">
                      <input type="checkbox" class="item-checkbox" data-item-id="${it.id}" ${isChecked ? 'checked' : ''} ${currentIsClosed ? 'disabled' : ''} style="width: 19px; height: 19px; cursor: ${currentIsClosed ? 'not-allowed' : 'pointer'}; accent-color: #10B981; flex-shrink: 0;" />
                      
                      <button type="button" class="btn-item-qty-chip" data-item-id="${it.id}" ${currentIsClosed ? 'disabled' : ''} title="Clique para ajustar quantidade" style="background: ${chipBg}; border: 1px solid ${chipBorder}; color: ${chipColor}; flex-shrink: 0;">
                        <span>${isPartial ? `${actQty}/${qty}` : qty}${unit !== 'UN' ? ` ${unit}` : ''}</span>
                      </button>

                      <span class="lista-item-title" style="flex: 1; min-width: 0; font-size: 0.92rem; font-weight: 600; color: var(--text-main); line-height: 1.3; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;" title="${escapeHTML(itemDesc)}">
                        ${escapeHTML(itemDesc)}
                      </span>
                    </div>

                    <!-- Line 2: Left (Best Quote / Chat) & Right (Price with larger font + Delete) -->
                    <div class="lista-item-line-2" style="display: flex; align-items: center; justify-content: space-between; gap: 0.5rem; width: 100%; min-width: 0; padding-left: 27px;">
                      
                      <!-- Left info: Best Quote Badge + Family Chat -->
                      <div style="display: flex; align-items: center; gap: 0.35rem; min-width: 0; flex: 1; overflow: hidden;">
                        ${bestQuoteInfo ? `
                          <span style="font-size: 0.72rem; color: #047857; font-weight: 700; display: inline-flex; align-items: center; gap: 0.25rem; background: rgba(16, 185, 129, 0.08); padding: 0.12rem 0.45rem; border-radius: var(--radius-pill); border: 1px solid rgba(16, 185, 129, 0.2); max-width: 100%; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;" title="Menor no ${escapeHTML(bestQuoteInfo.bestStore)}: R$ ${(bestQuoteInfo.lowestPrice).toFixed(2).replace('.', ',')}">
                            <span style="font-size: 0.72rem; flex-shrink: 0;">🏆</span>
                            <span style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">Menor no ${escapeHTML(formatStoreShortName(bestQuoteInfo.bestStore, 2))}: R$ ${(bestQuoteInfo.lowestPrice).toFixed(2).replace('.', ',')}</span>
                          </span>
                        ` : ''}

                        ${currentIsFamilyList ? `
                          <button class="btn-tag-item" data-item-id="${it.id}" data-item-desc="${escapeHTML(itemDesc)}" title="Comentar no chat" style="background: none; border: none; color: var(--color-secondary-text); cursor: pointer; padding: 0.15rem; display: flex; align-items: center; border-radius: var(--radius-sm); flex-shrink: 0;">
                            <span class="material-symbols-outlined" style="font-size: 15px;">chat</span>
                          </button>
                        ` : ''}
                      </div>

                      <!-- Right: Price with larger font + Delete Action -->
                      <div style="display: flex; align-items: center; gap: 0.45rem; flex-shrink: 0;">
                        <span class="font-mono font-bold" style="font-size: 0.98rem; font-weight: 800; color: ${priceColor}; text-align: right; white-space: nowrap; letter-spacing: -0.02em;">
                          R$ ${rowTotal}
                        </span>

                        ${!currentIsClosed ? `
                          <button class="btn-delete-item" data-item-id="${it.id}" title="Excluir item" style="background: none; border: none; color: var(--text-muted); cursor: pointer; padding: 0.15rem; display: flex; align-items: center; border-radius: var(--radius-sm);">
                            <span class="material-symbols-outlined" style="font-size: 15px;">close</span>
                          </button>
                        ` : ''}
                      </div>

                    </div>

                  </div>
                </div>
              `;
            };

            return `
              <div class="lista-category-group" style="display: flex; flex-direction: column; border-top: ${isFirstCategory ? 'none' : '1px solid var(--border-color-subtle, rgba(0, 0, 0, 0.06))'};">
                <!-- Discreet Category Header -->
                <div class="lista-cat-divider" style="background: var(--bg-category-header, #F5F4EE); padding: 0.48rem 0.85rem; display: flex; align-items: center; justify-content: space-between; border-bottom: 1px solid var(--border-color-subtle, rgba(0, 0, 0, 0.06));">
                  <div style="display: flex; align-items: center; gap: 0.35rem; min-width: 0;">
                    <span style="font-size: 0.95rem; line-height: 1;">${icon}</span>
                    <span style="font-weight: 800; font-size: 0.74rem; text-transform: uppercase; letter-spacing: 0.04em; color: var(--text-muted);">${escapeHTML(catName)}</span>
                    <span style="font-size: 0.68rem; color: var(--text-subtle);">(${pendingItems.length} restantes${completedItems.length > 0 ? `, ${completedItems.length} no carrinho` : ''})</span>
                  </div>
                  <span class="font-mono font-bold" style="font-size: 0.76rem; color: var(--text-muted); flex-shrink: 0;">R$ ${catTotal.toFixed(2).replace('.', ',')}</span>
                </div>

                <!-- Pending Items List -->
                <div style="display: flex; flex-direction: column;">
                  ${pendingItems.length > 0 ? pendingItems.map(it => renderItemRow(it, false)).join('') : `
                    <div class="text-muted text-center" style="padding: 0.5rem; font-size: 0.76rem; font-style: italic; border-bottom: 1px solid var(--border-color-subtle); background: var(--bg-surface);">
                      Categoria concluída ✓
                    </div>
                  `}
                </div>

                <!-- Collapsible Completed Items Accordion (Discreet) -->
                ${completedItems.length > 0 ? `
                  <div class="comprados-accordion" style="border-top: 1px dashed var(--border-color); background: var(--bg-category-header, #F5F4EE);">
                    <div class="comprados-accordion-header" data-cat="${escapeHTML(catName)}" style="padding: 0.4rem 0.85rem; font-size: 0.74rem; background: var(--bg-category-header, #F5F4EE);">
                      <div style="display: flex; align-items: center; gap: 0.3rem;">
                        <span class="material-symbols-outlined" style="font-size: 14px; color: #10B981;">check_circle</span>
                        <span>Comprados (${completedItems.length})</span>
                      </div>
                      <div style="display: flex; align-items: center; gap: 0.3rem;">
                        <span class="font-mono" style="font-size: 0.72rem;">R$ ${completedTotal.toFixed(2).replace('.', ',')}</span>
                        <span class="material-symbols-outlined accordion-chevron" style="font-size: 16px; transition: transform 0.2s ease; transform: ${isAccordionOpen ? 'rotate(180deg)' : 'rotate(0deg)'};">expand_more</span>
                      </div>
                    </div>
                    <div class="comprados-accordion-body ${isAccordionOpen ? '' : 'hidden'}" style="background: var(--bg-category-header, #F5F4EE);">
                      ${completedItems.map(it => renderItemRow(it, true)).join('')}
                    </div>
                  </div>
                ` : ''}
              </div>
            `;
          }).join('')}
        </div>
      `;
    }

    function refreshListArea() {
      if (window.location.hash !== `#lista/${id}`) return;
      const metrics = computeListMetricsAndGrouping();
      const currentItems = metrics.items;
      const currentCheckedCount = metrics.checkedCount;
      const currentProgressPct = metrics.progressPct;
      const currentEstVal = metrics.estVal;
      const currentActVal = metrics.actVal;
      const currentDiffVal = metrics.diffVal;
      const currentIsOverBudget = metrics.isOverBudget;
      const currentIsClosed = lista.status === 'fechada';

      // 1. Update summary card values in-place
      const estEl = container.querySelector('#summary-est-val');
      if (estEl) estEl.textContent = `R$ ${currentEstVal.toFixed(2).replace('.', ',')}`;

      const actEl = container.querySelector('#summary-act-val');
      if (actEl) actEl.textContent = `R$ ${currentActVal.toFixed(2).replace('.', ',')}`;

      const statusOrDiffEl = container.querySelector('#summary-status-or-diff');
      if (statusOrDiffEl) {
        if (currentActVal > 0 && currentEstVal > 0) {
          statusOrDiffEl.innerHTML = `
            <span class="text-muted" style="font-size: 0.7rem; display: block; margin-bottom: 0.15rem;">Desvio</span>
            <span class="font-mono font-bold" style="font-size: 1rem; color: ${currentIsOverBudget ? '#EF4444' : '#10B981'};">
              ${currentIsOverBudget ? '+' : ''}R$ ${currentDiffVal.toFixed(2).replace('.', ',')}
            </span>
          `;
        } else {
          statusOrDiffEl.innerHTML = `
            <span class="text-muted" style="font-size: 0.7rem; display: block; margin-bottom: 0.15rem;">Status</span>
            <span class="font-mono font-bold" style="font-size: 1rem; color: ${currentProgressPct === 100 ? '#10B981' : '#D97706'};">${currentCheckedCount}/${currentItems.length} itens</span>
          `;
        }
      }

      const progBar = container.querySelector('#summary-progress-bar');
      if (progBar) {
        progBar.style.width = `${currentProgressPct}%`;
        progBar.style.background = currentProgressPct === 100 ? '#10B981' : '#D97706';
      }

      // 2. Update sticky header counts
      const labelItemsCount = container.querySelector('#label-items-count');
      if (labelItemsCount) {
        labelItemsCount.textContent = `(${selectedStoreFilter !== 'all' ? `${Object.values(metrics.groupedItems).reduce((sum, cat) => sum + cat.length, 0)} no ${formatStoreShortName(selectedStoreFilter, 2)}` : currentItems.length})`;
      }

      const badgeComprados = container.querySelector('#badge-comprados-count');
      if (badgeComprados) {
        badgeComprados.textContent = `${currentCheckedCount} comprados`;
      }

      // 3. Update store filter option if present
      const storeSelect = container.querySelector('#select-list-store-filter');
      if (storeSelect) {
        const allOpt = storeSelect.querySelector('option[value="all"]');
        if (allOpt) allOpt.textContent = `🛒 Todos os Supermercados (${currentItems.length} itens)`;
      }

      // 4. Update items section innerHTML
      const itemsSection = container.querySelector('#items-list-section');
      if (itemsSection) {
        itemsSection.innerHTML = renderItemsAreaHTML(currentItems, metrics.groupedItems, metrics.itemBestStoreMap, currentIsClosed, isFamilyList);
        if (bindItemRowEvents) {
          bindItemRowEvents();
        }
        if (window._listaApplySearchFilter) {
          window._listaApplySearchFilter(currentSearchQuery);
        }
      }
    }

    const items = lista.items || [];
    const messages = lista.messages || [];
    const linkedNotas = lista.linkedNotas || [];
    const isClosed = lista.status === 'fechada';
    const typeLabel = (lista.type === 'mensal' || lista.tipo === 'mensal') ? 'Mensal' : ((lista.type === 'semanal' || lista.tipo === 'semanal') ? 'Semanal' : 'Avulsa');
    const typeBadgeColor = (lista.type === 'mensal' || lista.tipo === 'mensal') ? '#3B82F6' : '#10B981';

    const metrics = computeListMetricsAndGrouping();
    const {
      checkedCount,
      progressPct,
      estVal,
      actVal,
      diffVal,
      isOverBudget,
      itemBestStoreMap,
      storeSummaryMap,
      groupedItems
    } = metrics;

    // Read count of seen messages from localStorage to signal new messages
    const readCountKey = `nfce_chat_read_${lista.id}`;
    let readCount = Number(localStorage.getItem(readCountKey) || 0);
    if (!isChatCollapsed && messages.length > 0) {
      readCount = messages.length;
      localStorage.setItem(readCountKey, String(readCount));
    }
    const unreadCount = messages.length - readCount;
    const hasUnreadMessages = isFamilyList && isChatCollapsed && unreadCount > 0;

    const safeBottomPadding = isFamilyList ? (isChatCollapsed ? 90 : (chatHeight + 70)) : 85;

    container.innerHTML = `
      <!-- Container with safe bottom padding for floating chat and bottom bar -->
      <div id="lista-content-wrapper" style="padding-bottom: ${safeBottomPadding}px; transition: padding-bottom 0.15s ease;">
        
        <!-- Header & Back Button -->
        <div class="detail-header" style="display: flex; align-items: center; justify-content: space-between; gap: 0.5rem; margin-bottom: 1.25rem;">
          <div style="display: flex; align-items: center; gap: 0.75rem; min-width: 0; flex: 1;">
            <button class="detail-back" id="btn-back" style="background: var(--bg-surface-dim); border: none; color: var(--text-main); cursor: pointer; padding: 0.45rem; border-radius: 50%; display: flex; align-items: center; justify-content: center;">
              <span class="material-symbols-outlined" style="font-size: 20px;">arrow_back</span>
            </button>
            <div style="min-width: 0; flex: 1;">
              <div style="display: flex; align-items: center; gap: 0.45rem; flex-wrap: wrap;">
                <h2 style="margin: 0; font-size: clamp(1.2rem, 4vw, 1.5rem); font-weight: 800; color: var(--text-main); white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">
                  ${escapeHTML(lista.name || lista.nome || 'Lista de Compras')}
                </h2>
                <span style="font-size: 0.7rem; font-weight: 700; padding: 0.15rem 0.55rem; border-radius: var(--radius-pill); background: ${typeBadgeColor}20; color: ${typeBadgeColor}; text-transform: uppercase;">
                  ${typeLabel}
                </span>
                <span style="font-size: 0.7rem; font-weight: 700; padding: 0.15rem 0.55rem; border-radius: var(--radius-pill); background: ${isClosed ? 'rgba(107, 114, 128, 0.2)' : 'var(--color-primary-bg)'}; color: ${isClosed ? 'var(--text-muted)' : 'var(--color-primary-text)'}; border: 1px solid ${isClosed ? 'var(--border-color)' : 'var(--color-primary-border)'}; text-transform: uppercase;">
                  ${isClosed ? 'Fechada ✓' : 'Aberta'}
                </span>
              </div>
              <div class="text-muted" style="font-size: 0.78rem; margin-top: 0.2rem;">
                Ref: ${escapeHTML(lista.monthRef || lista.mesRef || 'Geral')} &bull; ${lista.scope === 'family' ? 'Compartilhada com Família' : 'Lista Pessoal'}
              </div>
            </div>
          </div>

          <a href="#lista-comparativo/${id}" id="btn-optimize-basket" class="btn btn-secondary" title="Comparar preço do carrinho entre supermercados" style="background: rgba(16, 185, 129, 0.12); border: 1px solid rgba(16, 185, 129, 0.35); color: #059669; padding: 0.45rem 0.85rem; font-size: 0.8rem; font-weight: 800; border-radius: var(--radius-pill); display: inline-flex; align-items: center; gap: 0.35rem; white-space: nowrap; flex-shrink: 0; text-decoration: none;">
            <span class="material-symbols-outlined" style="font-size: 18px;">price_check</span>
            <span>Onde Comprar?</span>
          </a>
        </div>

        <!-- Main Summary & Progress Card (Less Emphasis & Unified) -->
        <div id="lista-summary-card" class="chart-card" style="padding: 0.85rem 1rem; margin-bottom: 1rem; background: var(--bg-surface-alt); border: 1px solid var(--border-color); box-shadow: none;">
          <div style="display: flex; align-items: flex-start; justify-content: space-between; flex-wrap: wrap; gap: 0.75rem; margin-bottom: 0.65rem;">
            <div>
              <span class="text-muted" style="font-size: 0.7rem; display: block; margin-bottom: 0.15rem;">Total Estimado</span>
              <span id="summary-est-val" class="font-mono font-bold" style="font-size: 1.15rem; color: var(--text-main);">R$ ${estVal.toFixed(2).replace('.', ',')}</span>
            </div>
            <div>
              <span class="text-muted" style="font-size: 0.7rem; display: block; margin-bottom: 0.15rem;">Gasto Real</span>
              <span id="summary-act-val" class="font-mono font-bold" style="font-size: 1.15rem; color: #10B981;">R$ ${actVal.toFixed(2).replace('.', ',')}</span>
            </div>
            <div id="summary-status-or-diff">
              ${actVal > 0 && estVal > 0 ? `
                <span class="text-muted" style="font-size: 0.7rem; display: block; margin-bottom: 0.15rem;">Desvio</span>
                <span class="font-mono font-bold" style="font-size: 1rem; color: ${isOverBudget ? '#EF4444' : '#10B981'};">
                  ${isOverBudget ? '+' : ''}R$ ${diffVal.toFixed(2).replace('.', ',')}
                </span>
              ` : `
                <span class="text-muted" style="font-size: 0.7rem; display: block; margin-bottom: 0.15rem;">Status</span>
                <span class="font-mono font-bold" style="font-size: 1rem; color: ${progressPct === 100 ? '#10B981' : '#D97706'};">${checkedCount}/${items.length} itens</span>
              `}
            </div>
          </div>

          <div style="height: 6px; background: var(--border-color); border-radius: var(--radius-pill); overflow: hidden; margin-bottom: ${linkedNotas.length > 0 ? '0.65rem' : '0'};">
            <div id="summary-progress-bar" style="height: 100%; width: ${progressPct}%; background: ${progressPct === 100 ? '#10B981' : '#D97706'}; border-radius: var(--radius-pill); transition: width 0.3s ease;"></div>
          </div>

          ${linkedNotas.length > 0 ? `
            <div style="display: flex; align-items: center; justify-content: space-between; border-top: 1px dashed var(--border-color); padding-top: 0.65rem; margin-top: 0.25rem;">
              <div style="display: flex; align-items: center; gap: 0.35rem; color: #10B981; font-size: 0.75rem; font-weight: 700;">
                <span class="material-symbols-outlined" style="font-size: 16px;">receipt_long</span>
                <span>${linkedNotas.length} ${linkedNotas.length === 1 ? 'Cupom Vinculado' : 'Cupons Vinculados'}</span>
              </div>
              <span style="font-size: 0.75rem; font-weight: 700; color: #10B981;">Total: R$ ${linkedNotas.reduce((s, ln) => s + (ln.allocatedAmount || ln.nota?.totais?.valorTotal || ln.nota?.valorTotal || 0), 0).toFixed(2).replace('.', ',')}</span>
            </div>
          ` : ''}
        </div>

        <!-- Quick Add Item Card (If not closed) -->
        ${!isClosed ? `
          <div class="chart-card mb-lg" style="padding: 1.15rem 1.25rem; margin-bottom: 1.25rem; background: var(--bg-surface); border: 1px solid var(--border-color); border-radius: 1rem; box-shadow: var(--shadow-card);">
            <div style="font-size: 0.85rem; font-weight: 700; color: var(--text-main); margin-bottom: 0.75rem; display: flex; align-items: center; justify-content: space-between;">
              <div style="display: flex; align-items: center; gap: 0.4rem;">
                <span class="material-symbols-outlined" style="color: #D97706; font-size: 18px;">add_shopping_cart</span>
                <span>Adicionar Produto à Lista</span>
              </div>
              <span class="text-muted" style="font-size: 0.75rem;">Histórico ou Manual</span>
            </div>

            <!-- Unified Manual / Search Item Form -->
            <form id="form-quick-add-item" style="display: flex; flex-direction: column; gap: 0.65rem;">
              <!-- Unified Product Name & Autocomplete Field -->
              <div style="position: relative;">
                <div style="display: flex; align-items: center; background: var(--bg-surface-alt); border: 1px solid var(--border-color); border-radius: 0.65rem; padding: 0.5rem 0.65rem 0.5rem 0.85rem; gap: 0.5rem;">
                  <span class="material-symbols-outlined text-muted" style="font-size: 20px; flex-shrink: 0;">search</span>
                  <input type="text" id="input-manual-desc" required placeholder="Nome do produto ou buscar no histórico..." autocomplete="off" style="flex: 1; min-width: 0; border: none; outline: none; font-size: 0.9rem; font-weight: 600; background: transparent; color: var(--text-main); font-family: var(--font-body);" />
                  <button type="button" id="btn-clear-manual-desc" title="Limpar campo" style="display: none; background: none; border: none; padding: 0.15rem; color: var(--text-muted); cursor: pointer; border-radius: 50%; align-items: center; justify-content: center; flex-shrink: 0; transition: color 0.15s ease;">
                    <span class="material-symbols-outlined" style="font-size: 20px;">cancel</span>
                  </button>
                </div>
                <!-- Auto-complete Suggestions Dropdown -->
                <div id="product-search-suggestions" style="display: none; position: absolute; top: 100%; left: 0; right: 0; background: var(--bg-surface); border: 1px solid var(--border-color); border-radius: 0.65rem; box-shadow: var(--shadow-card); z-index: 100; max-height: 220px; overflow-y: auto; margin-top: 4px;"></div>
              </div>

              <!-- Stepper & Preço Estimado Row (Side by side on the same line) -->
              <div style="display: flex; gap: 0.5rem; align-items: flex-start;">
                <!-- Quantity Stepper Block -->
                <div style="flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 0.25rem;">
                  <span style="font-size: 0.72rem; font-weight: 700; color: var(--text-muted); text-transform: uppercase;">Quantidade</span>
                  <div style="display: flex; align-items: center; background: var(--bg-surface-alt); border: 1px solid var(--border-color); border-radius: 0.65rem; height: 38px; padding: 0 0.25rem;">
                    <button type="button" id="btn-manual-qty-minus" style="background: none; border: none; width: 32px; height: 32px; display: flex; align-items: center; justify-content: center; cursor: pointer; color: var(--text-main); border-radius: 0.4rem;">
                      <span class="material-symbols-outlined" style="font-size: 18px;">remove</span>
                    </button>
                    <input type="number" id="input-manual-qty" step="any" min="0.1" value="1" placeholder="1" style="flex: 1; min-width: 0; border: none; outline: none; background: transparent; text-align: center; font-weight: 700; font-size: 0.95rem; color: var(--text-main); font-family: var(--font-mono);" />
                    <button type="button" id="btn-manual-qty-plus" style="background: none; border: none; width: 32px; height: 32px; display: flex; align-items: center; justify-content: center; cursor: pointer; color: var(--text-main); border-radius: 0.4rem;">
                      <span class="material-symbols-outlined" style="font-size: 18px;">add</span>
                    </button>
                  </div>
                </div>

                <!-- Preço Estimado Input -->
                <div style="flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 0.25rem;">
                  <span style="font-size: 0.72rem; font-weight: 700; color: var(--text-muted); text-transform: uppercase;">Preço Est. (R$)</span>
                  <div style="display: flex; align-items: center; background: var(--bg-surface-alt); border: 1px solid var(--border-color); border-radius: 0.65rem; height: 38px; padding: 0 0.65rem;">
                    <span class="text-muted" style="font-size: 0.8rem; font-weight: 700; margin-right: 0.25rem;">R$</span>
                    <input type="number" id="input-manual-price" step="0.01" min="0" placeholder="0,00" style="flex: 1; min-width: 0; border: none; outline: none; background: transparent; font-weight: 700; font-size: 0.95rem; color: var(--text-main); font-family: var(--font-mono);" />
                  </div>
                </div>
              </div>

              <!-- Quick Presets Row -->
              <div style="display: flex; gap: 0.3rem; align-items: center; flex-wrap: wrap;">
                <span style="font-size: 0.7rem; color: var(--text-muted); margin-right: 0.15rem;">Atalhos:</span>
                <button type="button" class="btn-manual-qty-preset" data-delta="1" style="background: var(--bg-surface-dim); border: 1px solid var(--border-color); color: var(--text-main); border-radius: var(--radius-pill); font-size: 0.72rem; font-weight: 700; padding: 0.2rem 0.55rem; cursor: pointer; transition: all 0.15s ease;">+1</button>
                <button type="button" class="btn-manual-qty-preset" data-delta="2" style="background: var(--bg-surface-dim); border: 1px solid var(--border-color); color: var(--text-main); border-radius: var(--radius-pill); font-size: 0.72rem; font-weight: 700; padding: 0.2rem 0.55rem; cursor: pointer; transition: all 0.15s ease;">+2</button>
                <button type="button" class="btn-manual-qty-preset" data-delta="5" style="background: var(--bg-surface-dim); border: 1px solid var(--border-color); color: var(--text-main); border-radius: var(--radius-pill); font-size: 0.72rem; font-weight: 700; padding: 0.2rem 0.55rem; cursor: pointer; transition: all 0.15s ease;">+5</button>
                <button type="button" class="btn-manual-qty-preset" data-set="0.5" style="background: var(--bg-surface-dim); border: 1px solid var(--border-color); color: var(--text-muted); border-radius: var(--radius-pill); font-size: 0.72rem; font-weight: 700; padding: 0.2rem 0.55rem; cursor: pointer; transition: all 0.15s ease;">0.5 kg</button>
                <button type="button" class="btn-manual-qty-preset" data-set="1" style="background: var(--bg-surface-dim); border: 1px solid var(--border-color); color: var(--text-muted); border-radius: var(--radius-pill); font-size: 0.72rem; font-weight: 700; padding: 0.2rem 0.55rem; cursor: pointer; transition: all 0.15s ease;">Reset (1)</button>
              </div>

              <!-- Submit Button -->
              <button type="submit" class="btn btn-secondary" style="width: 100%; height: 42px; border-radius: var(--radius-pill); font-size: 0.9rem; font-weight: 700; display: inline-flex; align-items: center; justify-content: center; gap: 0.4rem; cursor: pointer; margin-top: 0.25rem; background: var(--bg-surface-dim); border: 1px solid var(--border-color); color: var(--text-main);">
                <span class="material-symbols-outlined" style="font-size: 18px; color: var(--color-primary-text, #2563EB);">add</span>
                <span>Adicionar à Lista</span>
              </button>
            </form>
          </div>
        ` : ''}

        <!-- Store Split Virtual Filter Select (When Cesta Mista / Multiple Stores Available) -->
        ${Object.keys(storeSummaryMap).length > 0 ? `
          <div class="chart-card mb-lg" style="padding: 0.85rem 1rem; margin-bottom: 1.15rem; background: var(--bg-surface); border: 1.5px solid ${selectedStoreFilter !== 'all' ? '#10B981' : 'var(--border-color)'}; border-radius: 1rem; box-shadow: var(--shadow-card);">
            <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 0.55rem; flex-wrap: wrap; gap: 0.4rem;">
              <label for="select-list-store-filter" style="font-size: 0.76rem; font-weight: 700; color: var(--text-muted); text-transform: uppercase; letter-spacing: 0.04em; display: flex; align-items: center; gap: 0.35rem; cursor: pointer;">
                <span class="material-symbols-outlined" style="font-size: 18px; color: #10B981;">shopping_bag</span>
                <span>Divisão de Compras por Mercado (Cesta Mista):</span>
              </label>
              <a href="#lista-comparativo/${id}" style="font-size: 0.75rem; font-weight: 700; color: #047857; text-decoration: none; display: inline-flex; align-items: center; gap: 0.2rem;">
                <span class="material-symbols-outlined" style="font-size: 15px;">analytics</span>
                <span>Ver Comparativo &rarr;</span>
              </a>
            </div>

            <div style="position: relative; width: 100%;">
              <select id="select-list-store-filter" class="custom-store-select" style="width: 100%; appearance: none; -webkit-appearance: none; background: var(--bg-surface-alt); border: 1.5px solid ${selectedStoreFilter !== 'all' ? '#10B981' : 'var(--border-color)'}; color: var(--text-main); font-size: 0.92rem; font-weight: 800; border-radius: 0.65rem; padding: 0.65rem 2.5rem 0.65rem 0.85rem; cursor: pointer; outline: none; transition: all 0.15s ease;">
                <option value="all" ${selectedStoreFilter === 'all' ? 'selected' : ''}>
                  🛒 Todos os Supermercados (${items.length} itens)
                </option>
                ${Object.entries(storeSummaryMap).map(([stName, stData]) => `
                  <option value="${escapeHTML(stName)}" ${selectedStoreFilter === stName ? 'selected' : ''}>
                    🏪 ${escapeHTML(stName)} (${stData.count} itens • R$ ${stData.total.toFixed(2).replace('.', ',')})
                  </option>
                `).join('')}
              </select>
              <span class="material-symbols-outlined" style="position: absolute; right: 0.75rem; top: 50%; transform: translateY(-50%); pointer-events: none; color: #10B981; font-size: 22px;">
                unfold_more
              </span>
            </div>

            ${selectedStoreFilter !== 'all' ? `
              <div style="margin-top: 0.45rem; padding-top: 0.45rem; border-top: 1px dashed var(--border-color); display: flex; justify-content: space-between; align-items: center; font-size: 0.76rem;">
                <span style="color: #047857; font-weight: 700;">
                  Exibindo apenas produtos recomendados para compra no <strong>${escapeHTML(selectedStoreFilter)}</strong>
                </span>
                <button type="button" id="btn-clear-store-filter" style="background: none; border: none; color: var(--text-muted); cursor: pointer; font-size: 0.74rem; font-weight: 700; text-decoration: underline;">
                  Mostrar Todos
                </button>
              </div>
            ` : ''}
          </div>
        ` : ''}

        <!-- Sticky Search & Items Section Header -->
        <div id="sticky-search-and-items-header" class="sticky-list-header-wrapper" style="position: sticky; top: var(--app-header-height, 56px); z-index: 40; background: var(--bg-canvas); backdrop-filter: blur(16px); -webkit-backdrop-filter: blur(16px); padding-top: 0.5rem; margin-bottom: 0.65rem;">
          
          <!-- Search Bar (Idêntico ao da tela de notas com botão X) -->
          <section class="search-bar" style="margin-bottom: 0.65rem; position: relative;">
            <span class="material-symbols-outlined search-icon">search</span>
            <input type="text" id="search-list-items-input" class="search-input" placeholder="Buscar produtos na lista..." value="${escapeHTML(currentSearchQuery)}" style="padding-right: 2.6rem; width: 100%; box-sizing: border-box;" autocomplete="off" />
            <button type="button" id="btn-clear-list-search" style="position: absolute; right: 0.75rem; top: 50%; transform: translateY(-50%); background: none; border: none; cursor: pointer; color: var(--text-muted); display: ${currentSearchQuery ? 'flex' : 'none'}; align-items: center; justify-content: center; padding: 0.25rem; border-radius: 50%;" title="Limpar busca">
              <span class="material-symbols-outlined" style="font-size: 1.15rem;">close</span>
            </button>
          </section>

          <!-- Sticky Items Section Header -->
          <div class="sticky-list-header" style="display: flex; justify-content: space-between; align-items: center; border-bottom: 1px solid var(--border-color-subtle); padding-bottom: 0.45rem;">
            <h3 class="chart-card-title" style="margin: 0; font-size: 1.1rem; font-weight: 800; color: var(--text-main); display: flex; align-items: center; gap: 0.35rem;">
              <span>Itens da Lista</span>
              <span id="label-items-count" style="font-size: 0.85rem; font-weight: 600; color: var(--text-muted);">(${selectedStoreFilter !== 'all' ? `${Object.values(groupedItems).reduce((sum, cat) => sum + cat.length, 0)} no ${formatStoreShortName(selectedStoreFilter, 2)}` : items.length})</span>
            </h3>
            <span id="badge-comprados-count" class="font-mono font-bold" style="font-size: 0.82rem; color: #D97706; background: var(--color-warning-bg); border: 1px solid var(--color-warning-border); padding: 0.2rem 0.65rem; border-radius: var(--radius-pill);">
              ${checkedCount} comprados
            </span>
          </div>
        </div>

        <!-- Section containing only the items list container (allows surgical in-place refresh) -->
        <div id="items-list-section">
          ${renderItemsAreaHTML(items, groupedItems, itemBestStoreMap, isClosed, isFamilyList)}
        </div>

        <!-- List Management & Closing Section at the Bottom -->
        <div class="chart-card" style="margin-top: 1.5rem; margin-bottom: 2rem; padding: 1.25rem; border: 1px dashed var(--border-color); background: var(--bg-surface-alt);">
          <h4 style="font-size: 0.95rem; font-weight: 800; color: var(--text-main); margin: 0 0 0.5rem 0; display: flex; align-items: center; gap: 0.4rem;">
            <span class="material-symbols-outlined" style="font-size: 20px; color: var(--text-muted);">tune</span>
            Gerenciamento da Lista
          </h4>

          <div style="display: flex; flex-direction: column; gap: 1.15rem; margin-top: 0.85rem;">
            ${!isClosed ? `
              <div style="display: flex; align-items: center; justify-content: space-between; gap: 1rem; flex-wrap: wrap; padding-bottom: 1rem; border-bottom: 1px solid var(--border-color);">
                <div style="flex: 1; min-width: 220px;">
                  <div style="font-size: 0.875rem; font-weight: 700; color: var(--text-main);">Finalizar / Fechamento da Lista</div>
                  <p class="text-muted" style="margin: 0.2rem 0 0 0; font-size: 0.78rem; line-height: 1.45;">
                    Consolida os valores finais planejados vs. comprados e bloqueia os itens para evitar alterações acidentais.
                  </p>
                </div>
                <button id="btn-fechamento" class="btn btn-secondary" style="padding: 0.55rem 1.15rem; font-size: 0.82rem; border-radius: var(--radius-pill); font-weight: 700; background: var(--color-primary-bg); color: var(--color-primary-text); border: 1px solid var(--color-primary-border); display: inline-flex; align-items: center; gap: 0.35rem; white-space: nowrap; cursor: pointer;">
                  <span class="material-symbols-outlined" style="font-size: 16px;">lock_clock</span>
                  Realizar Fechamento
                </button>
              </div>
            ` : `
              <div style="display: flex; align-items: center; justify-content: space-between; gap: 1rem; flex-wrap: wrap; padding-bottom: 1rem; border-bottom: 1px solid var(--border-color);">
                <div style="flex: 1; min-width: 220px;">
                  <div style="font-size: 0.875rem; font-weight: 700; color: var(--text-main); display: flex; align-items: center; gap: 0.35rem;">
                    <span class="material-symbols-outlined" style="color: #10B981; font-size: 18px;">check_circle</span>
                    Lista Fechada e Consolidada
                  </div>
                  <p class="text-muted" style="margin: 0.2rem 0 0 0; font-size: 0.78rem; line-height: 1.45;">
                    Esta lista foi concluída e os itens estão bloqueados para novas edições ou alterações no carrinho.
                  </p>
                </div>
              </div>
            `}

            <div style="display: flex; align-items: center; justify-content: space-between; gap: 1rem; flex-wrap: wrap;">
              <div style="flex: 1; min-width: 220px;">
                <div style="font-size: 0.875rem; font-weight: 700; color: var(--color-danger);">Excluir Lista Permanentemente</div>
                <p class="text-muted" style="margin: 0.2rem 0 0 0; font-size: 0.78rem; line-height: 1.45;">
                  Remove a lista de compras, todos os itens e o histórico de recados associado.
                </p>
              </div>
              ${checkedCount === 0 && !isClosed ? `
                <button id="btn-delete-lista" class="btn" style="padding: 0.55rem 1.15rem; font-size: 0.82rem; border-radius: var(--radius-pill); font-weight: 700; background: var(--color-danger-bg); color: var(--color-danger); border: 1px solid var(--color-danger-border); display: inline-flex; align-items: center; gap: 0.35rem; white-space: nowrap; cursor: pointer;">
                  <span class="material-symbols-outlined" style="font-size: 16px;">delete</span>
                  Excluir Lista
                </button>
              ` : (checkedCount > 0 ? `
                <div style="font-size: 0.76rem; font-weight: 700; color: var(--text-muted); display: inline-flex; align-items: center; gap: 0.35rem; background: var(--bg-surface-dim); border: 1px solid var(--border-color); padding: 0.4rem 0.85rem; border-radius: var(--radius-pill);">
                  <span class="material-symbols-outlined" style="font-size: 16px; color: #D97706;">lock</span>
                  Possui itens no carrinho (não pode ser excluída)
                </div>
              ` : '')}
            </div>
          </div>
        </div>

      </div>

      <!-- Fechamento / Finalize Confirmation Modal -->
      <div id="fechamento-lista-modal" class="modal-overlay hidden" style="position: fixed; inset: 0; z-index: 9999; align-items: center; justify-content: center; padding: 1rem;">
        <div class="modal-card" style="background: var(--bg-surface); border-radius: 1.25rem; border: 1px solid var(--border-color); box-shadow: var(--shadow-card); max-width: 420px; width: 100%; padding: 1.75rem 1.5rem; text-align: center;">
          <div style="width: 52px; height: 52px; border-radius: 50%; background: var(--color-success-bg, rgba(16, 185, 129, 0.12)); color: var(--color-success, #10B981); display: flex; align-items: center; justify-content: center; margin: 0 auto 1rem auto; border: 1px solid var(--color-success-border, rgba(16, 185, 129, 0.3));">
            <span class="material-symbols-outlined" style="font-size: 28px;">lock_clock</span>
          </div>
          <h3 style="font-weight: 800; font-size: 1.15rem; color: var(--text-main); margin: 0 0 0.4rem 0;">Realizar Fechamento da Lista?</h3>
          <p class="text-muted" style="font-size: 0.85rem; line-height: 1.45; margin: 0 0 1.25rem 0;">
            Deseja realizar o Fechamento desta lista? Os totais serão consolidados para métricas financeiras e o status passará para finalizado.
          </p>
          <div style="background: var(--bg-surface-alt); padding: 0.75rem; border-radius: 0.75rem; border: 1px solid var(--border-color); margin-bottom: 1.25rem; text-align: left;">
            <label style="display: flex; align-items: flex-start; gap: 0.5rem; cursor: pointer; margin: 0;">
              <input type="checkbox" id="checkbox-move-pending" style="margin-top: 0.2rem; width: 16px; height: 16px;">
              <div style="display: flex; flex-direction: column;">
                <span style="font-size: 0.9rem; font-weight: 600; color: var(--text-main);">Mover pendentes para Nova Lista</span>
                <span style="font-size: 0.75rem; color: var(--text-muted);">Itens não comprados serão transferidos</span>
              </div>
            </label>
            <div id="new-lista-name-container" style="display: none; margin-top: 0.75rem;">
               <input type="text" id="input-new-lista-name" style="width: 100%; font-size: 0.85rem; padding: 0.4rem 0.6rem; border-radius: 0.5rem; border: 1px solid var(--border-color); background: var(--bg-surface); color: var(--text-main);" placeholder="Nome da nova lista">
            </div>
          </div>
          <div style="display: flex; gap: 0.5rem;">
            <button type="button" class="btn btn-secondary" id="btn-cancel-fechamento-lista" style="flex: 1; border-radius: var(--radius-pill); font-weight: 700; height: 38px;">
              Cancelar
            </button>
            <button type="button" class="btn btn-primary" id="btn-confirm-fechamento-lista" style="flex: 1; border-radius: var(--radius-pill); font-weight: 700; height: 38px; background: #10B981; border-color: #10B981; color: #FFF; display: inline-flex; align-items: center; justify-content: center; gap: 0.35rem;">
              <span class="material-symbols-outlined" style="font-size: 18px;">check</span>
              <span>Confirmar</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Delete List Confirmation Modal -->
      <div id="delete-lista-modal" class="modal-overlay hidden" style="position: fixed; inset: 0; z-index: 9999; align-items: center; justify-content: center; padding: 1rem;">
        <div class="modal-card" style="background: var(--bg-surface); border-radius: 1.25rem; border: 1px solid var(--border-color); box-shadow: var(--shadow-card); max-width: 400px; width: 100%; padding: 1.75rem 1.5rem; text-align: center;">
          <div style="width: 52px; height: 52px; border-radius: 50%; background: var(--color-danger-bg); color: var(--color-danger); display: flex; align-items: center; justify-content: center; margin: 0 auto 1rem auto; border: 1px solid var(--color-danger-border);">
            <span class="material-symbols-outlined" style="font-size: 28px;">delete_forever</span>
          </div>
          <h3 style="font-weight: 800; font-size: 1.15rem; color: var(--text-main); margin: 0 0 0.4rem 0;">Excluir Lista de Compras?</h3>
          <p class="text-muted" style="font-size: 0.85rem; line-height: 1.45; margin: 0 0 1.25rem 0;">
            Esta ação removerá permanentemente a lista <strong>"${escapeHTML(lista.name || lista.nome || 'Lista')}"</strong>, todos os seus produtos e o histórico de recados do sistema.
          </p>
          <div style="display: flex; gap: 0.5rem;">
            <button class="btn btn-secondary" id="btn-cancel-delete-lista" style="flex: 1; border-radius: var(--radius-pill); font-weight: 700; height: 38px;">
              Cancelar
            </button>
            <button class="btn btn-danger" id="btn-confirm-delete-lista" style="flex: 1; border-radius: var(--radius-pill); font-weight: 700; background: #DC2626; color: #FFF; height: 38px;">
              Sim, Excluir
            </button>
          </div>
        </div>
      </div>

      <!-- Delete Item Confirmation Modal (Standard App Modal) -->
      <div id="delete-item-modal" class="modal-overlay hidden" style="position: fixed; inset: 0; z-index: 9999; align-items: center; justify-content: center; padding: 1rem;">
        <div class="modal-card" style="background: var(--bg-surface); border-radius: 1.25rem; border: 1px solid var(--border-color); box-shadow: var(--shadow-card); max-width: 380px; width: 100%; padding: 1.75rem 1.5rem; text-align: center;">
          <div style="width: 48px; height: 48px; border-radius: 50%; background: var(--color-danger-bg); color: var(--color-danger); display: flex; align-items: center; justify-content: center; margin: 0 auto 1rem auto; border: 1px solid var(--color-danger-border);">
            <span class="material-symbols-outlined" style="font-size: 24px;">delete</span>
          </div>
          <h3 style="font-weight: 800; font-size: 1.1rem; color: var(--text-main); margin: 0 0 0.4rem 0;">Excluir Produto?</h3>
          <p class="text-muted" style="font-size: 0.85rem; line-height: 1.45; margin: 0 0 1.25rem 0;">
            Deseja realmente remover o produto <strong id="delete-item-modal-desc" style="color: var(--text-main);">-</strong> desta lista?
          </p>
          <div style="display: flex; gap: 0.5rem;">
            <button type="button" class="btn btn-secondary" id="btn-cancel-delete-item" style="flex: 1; border-radius: var(--radius-pill); font-weight: 700; height: 38px;">
              Cancelar
            </button>
            <button type="button" class="btn btn-danger" id="btn-confirm-delete-item" style="flex: 1; border-radius: var(--radius-pill); font-weight: 700; background: #DC2626; color: #FFF; height: 38px;">
              Sim, Excluir
            </button>
          </div>
        </div>
      </div>

      <!-- Modal: Confirmar Compra OU Editar Item na Lista -->
      <div id="modal-confirm-buy" class="modal-overlay hidden" style="position: fixed; inset: 0; z-index: 9999; align-items: center; justify-content: center; padding: 1rem;">
        <div class="modal-card" style="background: var(--bg-surface); border-radius: 1.25rem; border: 1px solid var(--border-color); box-shadow: var(--shadow-card); max-width: 440px; width: 100%; padding: 1.4rem; text-align: left; box-sizing: border-box;">
          
          <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 0.85rem;">
            <div style="display: flex; align-items: center; gap: 0.5rem;">
              <div style="width: 36px; height: 36px; border-radius: 50%; background: var(--color-primary-bg); color: var(--color-primary-text); display: flex; align-items: center; justify-content: center;">
                <span class="material-symbols-outlined" style="font-size: 20px;" id="buy-modal-header-icon">shopping_cart_checkout</span>
              </div>
              <h3 style="font-weight: 800; font-size: 1.05rem; color: var(--text-main); margin: 0;" id="buy-modal-title">Item da Lista</h3>
            </div>
            <button type="button" id="btn-close-buy-modal" aria-label="Fechar" style="background: none; border: none; color: var(--text-muted); cursor: pointer; padding: 0.2rem; display: flex; align-items: center;">
              <span class="material-symbols-outlined">close</span>
            </button>
          </div>

          <!-- Product Name Banner -->
          <div id="buy-modal-item-info" style="background: var(--bg-surface-alt); border: 1px solid var(--border-color); border-radius: 0.85rem; padding: 0.75rem 1rem; margin-bottom: 1rem;">
            <div id="buy-modal-desc" style="font-weight: 800; font-size: 0.95rem; color: var(--text-main); line-height: 1.35;">-</div>
            <div class="text-muted" style="font-size: 0.8rem; margin-top: 0.25rem;">
              Planejado: <strong id="buy-modal-planned" style="color: var(--text-main);">-</strong> &bull; Estimado: <strong id="buy-modal-est-price" style="color: var(--text-main);">-</strong>
            </div>
          </div>

          <!-- Mode Selector Tabs (Abas no Topo Perfeitamente Alinhadas) -->
          <div id="buy-modal-mode-tabs" style="display: grid; grid-template-columns: 1fr 1fr; gap: 4px; background: var(--bg-surface-alt, #F1F5F9); border: 1px solid var(--border-color); border-radius: var(--radius-pill, 9999px); padding: 4px; margin-bottom: 1.15rem; box-sizing: border-box;">
            <button type="button" class="modal-mode-pill active" id="tab-mode-buy" data-mode="buy" style="border: none; border-radius: var(--radius-pill, 9999px); height: 36px; display: inline-flex; align-items: center; justify-content: center; gap: 0.35rem; font-size: 0.82rem; font-weight: 800; cursor: pointer; transition: all 150ms ease; background: var(--text-main, #0F172A); color: #FFF; box-shadow: 0 1px 3px rgba(0,0,0,0.15);">
              <span class="material-symbols-outlined" style="font-size: 17px;">shopping_cart</span>
              <span>Confirmar Compra</span>
            </button>
            <button type="button" class="modal-mode-pill" id="tab-mode-edit" data-mode="edit" style="border: none; border-radius: var(--radius-pill, 9999px); height: 36px; display: inline-flex; align-items: center; justify-content: center; gap: 0.35rem; font-size: 0.82rem; font-weight: 800; cursor: pointer; transition: all 150ms ease; background: transparent; color: var(--text-muted, #64748B);">
              <span class="material-symbols-outlined" style="font-size: 17px;">edit_note</span>
              <span>Editar na Lista</span>
            </button>
          </div>

          <!-- TAB 1: CONFIRMAR COMPRA -->
          <form id="form-confirm-buy" style="display: block;">
            <!-- Stepper de Quantidade Comprada -->
            <label style="display: block; font-size: 0.78rem; font-weight: 700; color: var(--text-muted); text-transform: uppercase; margin-bottom: 0.35rem;">
              Quantidade Comprada:
            </label>
            <div style="display: flex; align-items: center; justify-content: center; gap: 0.65rem; margin-bottom: 0.55rem; width: 100%; box-sizing: border-box;">
              <button type="button" id="btn-buy-qty-minus" style="width: 44px; height: 42px; min-width: 44px; padding: 0; border-radius: 0.65rem; font-size: 1.35rem; font-weight: 800; border: 1px solid var(--border-color); background: var(--bg-surface-alt); color: var(--text-main); display: inline-flex; align-items: center; justify-content: center; cursor: pointer; user-select: none; outline: none;">
                −
              </button>
              <input type="number" id="input-buy-qty" step="any" min="0" required inputmode="decimal" style="width: 80px; max-width: 100px; height: 42px; text-align: center; font-size: 1.25rem; font-weight: 800; border-radius: 0.65rem; border: 1px solid var(--border-color); background: var(--bg-surface-dim); color: var(--text-main); outline: none; font-family: var(--font-mono); box-sizing: border-box;" />
              <button type="button" id="btn-buy-qty-plus" style="width: 44px; height: 42px; min-width: 44px; padding: 0; border-radius: 0.65rem; font-size: 1.35rem; font-weight: 800; border: 1px solid var(--border-color); background: var(--bg-surface-alt); color: var(--text-main); display: inline-flex; align-items: center; justify-content: center; cursor: pointer; user-select: none; outline: none;">
                +
              </button>
            </div>

            <!-- Atalhos de Quantidade Comprada Dinâmicos -->
            <div id="buy-modal-presets-container" style="display: flex; gap: 0.35rem; justify-content: center; flex-wrap: wrap; margin-bottom: 1.15rem;">
              <!-- Populated dynamically with smart quantity suggestions -->
            </div>

            <!-- Preço Real (Opcional) -->
            <div style="margin-bottom: 1.25rem;">
              <label style="display: block; font-size: 0.78rem; font-weight: 700; color: var(--text-muted); text-transform: uppercase; margin-bottom: 0.35rem;">
                Preço Real Pago por Unidade (Opcional):
              </label>
              <div style="display: flex; align-items: center; gap: 0.5rem; background: var(--bg-surface-dim); border: 1px solid var(--border-color); border-radius: 0.65rem; padding: 0 0.85rem; height: 42px; box-sizing: border-box;">
                <span class="text-muted font-bold font-mono" style="font-size: 0.9rem;">R$</span>
                <input type="text" inputmode="decimal" id="input-buy-price" placeholder="0,00" style="flex: 1; min-width: 0; height: 100%; border: none; background: transparent; color: var(--text-main); font-size: 1.05rem; font-weight: 700; outline: none; font-family: var(--font-mono);" />
              </div>
            </div>

            <!-- Actions -->
            <div style="display: flex; gap: 0.5rem;">
              <button type="button" id="btn-uncheck-item" class="btn btn-secondary" style="flex: 1; border-radius: var(--radius-pill); font-weight: 700; font-size: 0.82rem; height: 42px;">
                Desmarcar
              </button>
              <button type="submit" class="btn btn-primary" style="flex: 1.3; border-radius: var(--radius-pill); font-weight: 700; font-size: 0.85rem; height: 42px; display: inline-flex; align-items: center; justify-content: center; gap: 0.35rem;">
                <span class="material-symbols-outlined" style="font-size: 18px;">check</span>
                <span>Salvar no Carrinho</span>
              </button>
            </div>
          </form>

          <!-- TAB 2: EDITAR ITEM NA LISTA -->
          <form id="form-edit-list-item" style="display: none;">
            <!-- Stepper de Quantidade Planejada -->
            <label style="display: block; font-size: 0.78rem; font-weight: 700; color: var(--text-muted); text-transform: uppercase; margin-bottom: 0.35rem;">
              Quantidade Planejada:
            </label>
            <div style="display: flex; align-items: center; justify-content: center; gap: 0.65rem; margin-bottom: 0.55rem; width: 100%; box-sizing: border-box;">
              <button type="button" id="btn-edit-qty-minus" style="width: 44px; height: 42px; min-width: 44px; padding: 0; border-radius: 0.65rem; font-size: 1.35rem; font-weight: 800; border: 1px solid var(--border-color); background: var(--bg-surface-alt); color: var(--text-main); display: inline-flex; align-items: center; justify-content: center; cursor: pointer; user-select: none; outline: none;">
                −
              </button>
              <input type="number" id="input-edit-planned-qty" step="any" min="0.1" required inputmode="decimal" style="width: 80px; max-width: 100px; height: 42px; text-align: center; font-size: 1.25rem; font-weight: 800; border-radius: 0.65rem; border: 1px solid var(--border-color); background: var(--bg-surface-dim); color: var(--text-main); outline: none; font-family: var(--font-mono); box-sizing: border-box;" />
              <button type="button" id="btn-edit-qty-plus" style="width: 44px; height: 42px; min-width: 44px; padding: 0; border-radius: 0.65rem; font-size: 1.35rem; font-weight: 800; border: 1px solid var(--border-color); background: var(--bg-surface-alt); color: var(--text-main); display: inline-flex; align-items: center; justify-content: center; cursor: pointer; user-select: none; outline: none;">
                +
              </button>
            </div>

            <!-- Atalhos de Quantidade Planejada -->
            <div style="display: flex; gap: 0.35rem; justify-content: center; flex-wrap: wrap; margin-bottom: 1.15rem;">
              <button type="button" class="btn-edit-preset" data-delta="1" style="background: var(--bg-surface-dim); border: 1px solid var(--border-color); color: var(--text-main); border-radius: var(--radius-pill); font-size: 0.74rem; font-weight: 700; padding: 0.25rem 0.65rem; cursor: pointer; transition: all 0.15s ease;">+1</button>
              <button type="button" class="btn-edit-preset" data-delta="2" style="background: var(--bg-surface-dim); border: 1px solid var(--border-color); color: var(--text-main); border-radius: var(--radius-pill); font-size: 0.74rem; font-weight: 700; padding: 0.25rem 0.65rem; cursor: pointer; transition: all 0.15s ease;">+2</button>
              <button type="button" class="btn-edit-preset" data-delta="5" style="background: var(--bg-surface-dim); border: 1px solid var(--border-color); color: var(--text-main); border-radius: var(--radius-pill); font-size: 0.74rem; font-weight: 700; padding: 0.25rem 0.65rem; cursor: pointer; transition: all 0.15s ease;">+5</button>
              <button type="button" class="btn-edit-preset" data-set="0.5" style="background: var(--bg-surface-dim); border: 1px solid var(--border-color); color: var(--text-muted); border-radius: var(--radius-pill); font-size: 0.74rem; font-weight: 700; padding: 0.25rem 0.65rem; cursor: pointer; transition: all 0.15s ease;">0.5 kg</button>
              <button type="button" class="btn-edit-preset" data-set="1" style="background: var(--bg-surface-dim); border: 1px solid var(--border-color); color: var(--text-muted); border-radius: var(--radius-pill); font-size: 0.74rem; font-weight: 700; padding: 0.25rem 0.65rem; cursor: pointer; transition: all 0.15s ease;">Reset (1)</button>
            </div>

            <!-- Preço Estimado da Lista -->
            <div style="margin-bottom: 1.25rem;">
              <label style="display: block; font-size: 0.78rem; font-weight: 700; color: var(--text-muted); text-transform: uppercase; margin-bottom: 0.35rem;">
                Preço Estimado por Unidade:
              </label>
              <div style="display: flex; align-items: center; gap: 0.5rem; background: var(--bg-surface-dim); border: 1px solid var(--border-color); border-radius: 0.65rem; padding: 0 0.85rem; height: 42px; box-sizing: border-box;">
                <span class="text-muted font-bold font-mono" style="font-size: 0.9rem;">R$</span>
                <input type="text" inputmode="decimal" id="input-edit-estimated-price" placeholder="0,00" style="flex: 1; min-width: 0; height: 100%; border: none; background: transparent; color: var(--text-main); font-size: 1.05rem; font-weight: 700; outline: none; font-family: var(--font-mono);" />
              </div>
            </div>

            <!-- Actions -->
            <div style="display: flex; gap: 0.5rem;">
              <button type="button" id="btn-cancel-edit-item" class="btn btn-secondary" style="flex: 1; border-radius: var(--radius-pill); font-weight: 700; font-size: 0.82rem; height: 42px;">
                Cancelar
              </button>
              <button type="submit" class="btn btn-primary" style="flex: 1.3; border-radius: var(--radius-pill); font-weight: 700; font-size: 0.85rem; height: 42px; display: inline-flex; align-items: center; justify-content: center; gap: 0.35rem;">
                <span class="material-symbols-outlined" style="font-size: 18px;">save</span>
                <span>Atualizar na Lista</span>
              </button>
            </div>
          </form>

        </div>
      </div>

      <!-- Floating Suspended Chat Widget (Docked above bottom bar - Only for Family Lists) -->
      ${isFamilyList ? `
        <div id="floating-chat-widget" class="${isChatCollapsed ? 'chat-collapsed' : 'chat-expanded'}">
          ${isChatCollapsed ? `
            <!-- Collapsed Pill Format (Aligned to the right at the same anchor point) -->
            <button type="button" id="btn-open-chat-pill" class="chat-pill-trigger" title="Abrir conversa da lista">
              <span class="material-symbols-outlined chat-pill-icon">chat</span>
              <span class="chat-pill-label">Conversa</span>
              ${hasUnreadMessages ? `
                <span class="chat-unread-badge chat-pill-unread">
                  <span class="chat-pulse-dot" style="width: 6px; height: 6px;"></span>
                  ${unreadCount}
                </span>
              ` : (messages.length > 0 ? `
                <span class="chat-pill-count">
                  ${messages.length}
                </span>
              ` : '')}
            </button>
          ` : `
            <!-- Collapsible Chat Header with Drag Handle ("Traço") -->
            <div id="chat-toggle-header" class="chat-toggle-header" style="background: var(--bg-chat-bar); border-bottom: 1px solid var(--border-chat-bar);">
              
              <!-- Top Drag Handle Bar ("Traço") -->
              <div id="chat-drag-handle" class="chat-drag-handle">
                <div class="chat-drag-bar"></div>
              </div>

              <div style="display: grid; grid-template-columns: 32px 1fr 32px; align-items: center; padding: 0.15rem 0.75rem 0.4rem 0.75rem;">
                <div></div> <!-- Espaçador para centralização exata -->

                <!-- Título Centralizado da Conversa -->
                <div style="display: flex; align-items: center; justify-content: center; gap: 0.4rem;">
                  <span class="material-symbols-outlined" style="color: var(--color-primary-text, #2563EB); font-size: 16px;">forum</span>
                  <span style="font-weight: 800; font-size: 0.86rem; color: var(--text-main); letter-spacing: -0.01em;">Conversa da Lista</span>
                  ${messages.length > 0 ? `
                    <span style="background: var(--bg-surface-dim); border: 1px solid var(--border-color); color: var(--text-muted); font-size: 0.72rem; font-weight: 700; padding: 0.1rem 0.45rem; border-radius: var(--radius-pill);">
                      ${messages.length}
                    </span>
                  ` : ''}
                </div>

                <!-- Botão de Expandir/Recolher à Direita -->
                <div style="display: flex; justify-content: flex-end;">
                  <button id="btn-toggle-chat" style="background: none; border: none; color: var(--text-muted); cursor: pointer; display: flex; align-items: center; justify-content: center; padding: 0.2rem; border-radius: 50%;" aria-label="Fechar conversa">
                    <span class="material-symbols-outlined" style="font-size: 22px; color: var(--text-muted); transition: transform 0.2s ease;">keyboard_arrow_down</span>
                  </button>
                </div>
              </div>
            </div>
          `}

          <!-- Chat Body & Messages (Rendered when expanded) -->
          ${!isChatCollapsed ? `
            <div id="chat-expanded-body" class="chat-expanded-body" style="height: ${chatHeight}px;">
              
              <!-- Message Stream -->
              <div id="chat-messages-stream" class="chat-messages-stream">
                ${messages.length === 0 ? `
                  <div class="text-muted text-center py-sm" style="font-size: 0.85rem; margin: auto 0;">
                    Nenhum recado ainda. Toque em "Mencionar" em um item da lista ou escreva uma mensagem para quem for ao mercado!
                  </div>
                ` : messages.map(m => {
                  let msgText = m.message || '';
                  let itemTag = '';
                  let replyTag = '';

                  // Extract quoted item [🏷️ Item]
                  if (msgText.startsWith('[🏷️ ')) {
                    const endIdx = msgText.indexOf(']');
                    if (endIdx !== -1) {
                      itemTag = msgText.slice(1, endIdx);
                      msgText = msgText.slice(endIdx + 1).trim();
                    }
                  }

                  // Extract quoted reply [↩️ User: "Text"]
                  if (msgText.startsWith('[↩️ ')) {
                    const endIdx = msgText.indexOf(']');
                    if (endIdx !== -1) {
                      replyTag = msgText.slice(1, endIdx);
                      msgText = msgText.slice(endIdx + 1).trim();
                    }
                  }

                  return `
                    <div class="chat-message-bubble" data-msg-id="${m.id}">
                      
                      <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.35rem; font-size: 0.75rem;">
                        <span class="chat-author-name">${escapeHTML(m.userName || 'Membro')}</span>
                        <div style="display: flex; align-items: center; gap: 0.4rem;">
                          <span class="text-muted" style="font-size: 0.72rem;">${new Date(m.createdAt || Date.now()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                          <button class="btn-reply-msg" data-msg-id="${m.id}" data-user="${escapeHTML(m.userName || 'Membro')}" data-text="${escapeHTML(msgText)}" title="Responder mensagem" style="background: none; border: none; color: var(--text-muted); cursor: pointer; display: flex; align-items: center; gap: 0.15rem; font-size: 0.7rem; font-weight: 700; padding: 0.15rem 0.35rem; border-radius: 0.35rem;">
                            <span class="material-symbols-outlined" style="font-size: 13px;">reply</span> Responder
                          </button>
                        </div>
                      </div>

                      ${replyTag ? `
                        <div class="chat-reply-tag">
                          ${escapeHTML(replyTag)}
                        </div>
                      ` : ''}

                      ${itemTag ? `
                        <div class="chat-item-tag">
                          ${escapeHTML(itemTag)}
                        </div>
                      ` : ''}

                      <div class="chat-message-text">${escapeHTML(msgText)}</div>
                    </div>
                  `;
                }).join('')}
              </div>

              <!-- Active Tagged Item Pill (If an item is selected above) -->
              ${quotedItem ? `
                <div id="quoted-item-bar" class="chat-quoted-bar">
                  <div style="display: flex; align-items: center; gap: 0.35rem; min-width: 0;">
                    <span class="material-symbols-outlined" style="font-size: 15px;">sell</span>
                    <span style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">Falando de: <strong>${escapeHTML(quotedItem.description)}</strong></span>
                  </div>
                  <button id="btn-cancel-quote" style="background: none; border: none; color: currentColor; cursor: pointer; padding: 0.15rem;">
                    <span class="material-symbols-outlined" style="font-size: 16px;">close</span>
                  </button>
                </div>
              ` : ''}

              <!-- Active Reply Preview Bar (If replying to a message) -->
              ${replyToMessage ? `
                <div id="reply-preview-bar" class="chat-reply-preview-bar">
                  <div style="min-width: 0; flex: 1;">
                    <div style="font-weight: 700; color: var(--color-secondary-text); display: flex; align-items: center; gap: 0.25rem;">
                      <span class="material-symbols-outlined" style="font-size: 14px;">reply</span>
                      Respondendo a ${escapeHTML(replyToMessage.userName)}
                    </div>
                    <div class="text-muted" style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 320px; font-size: 0.75rem;">
                      ${escapeHTML(replyToMessage.text)}
                    </div>
                  </div>
                  <button id="btn-cancel-reply" style="background: none; border: none; color: var(--text-muted); cursor: pointer; padding: 0.15rem;">
                    <span class="material-symbols-outlined" style="font-size: 16px;">close</span>
                  </button>
                </div>
              ` : ''}

              <!-- Send Message Row with Embedded Arrow/Enter Button -->
              <form id="form-send-chat-msg" class="chat-send-form">
                <div class="chat-input-wrapper">
                  <input type="text" id="input-chat-msg" required class="chat-input" placeholder="${quotedItem ? `Comente sobre ${escapeHTML(quotedItem.description)}...` : (replyToMessage ? `Responder para ${escapeHTML(replyToMessage.userName)}...` : 'Escreva um recado para a família...')}" />
                  <button type="submit" id="btn-send-chat-msg" class="btn-send-chat-embedded" title="Enviar recado (Enter)">
                    <span class="material-symbols-outlined" style="font-size: 16px;">arrow_upward</span>
                  </button>
                </div>
              </form>
            </div>
          ` : ''}
        </div>
      ` : ''}
    `;

    bindEvents();
  }

  function bindEvents() {
    const items = lista.items || [];
    const isClosed = lista.status === 'fechada';

    // Store Split Virtual Filter Select
    const selectStoreFilter = container.querySelector('#select-list-store-filter');
    if (selectStoreFilter) {
      selectStoreFilter.addEventListener('change', (e) => {
        const store = e.target.value || 'all';
        selectedStoreFilter = store;
        sessionStorage.setItem('active_list_store_filter', store);
        render();
      });
    }

    const btnClearStore = container.querySelector('#btn-clear-store-filter');
    if (btnClearStore) {
      btnClearStore.addEventListener('click', () => {
        selectedStoreFilter = 'all';
        sessionStorage.setItem('active_list_store_filter', 'all');
        render();
      });
    }

    // Real-time search filter for list items
    const searchInput = container.querySelector('#search-list-items-input');
    const btnClearSearch = container.querySelector('#btn-clear-list-search');

    if (searchInput) {
      const applySearchFilter = (q) => {
        currentSearchQuery = q;
        const norm = q.trim().toLowerCase();
        if (btnClearSearch) {
          btnClearSearch.style.display = norm ? 'flex' : 'none';
        }

        const categoryGroups = container.querySelectorAll('.lista-category-group');
        let totalMatches = 0;

        categoryGroups.forEach(group => {
          const itemWrappers = group.querySelectorAll('.swipe-item-wrapper');
          let groupMatches = 0;

          itemWrappers.forEach(wrap => {
            const itemName = (wrap.getAttribute('data-item-name') || '').toLowerCase();
            const barcode = (wrap.getAttribute('data-barcode') || '').toLowerCase();
            const descEl = wrap.querySelector('.lista-item-title') || wrap.querySelector('.lista-item-desc span');
            const descText = (descEl ? descEl.textContent : '').toLowerCase();
            
            const matches = !norm || itemName.includes(norm) || descText.includes(norm) || barcode.includes(norm);
            wrap.style.display = matches ? '' : 'none';
            if (matches) {
              groupMatches++;
              totalMatches++;
            }
          });

          // Show category header/group only if it contains matching items
          group.style.display = (groupMatches > 0 || !norm) ? '' : 'none';
        });

        // Toggle No Results Message & Items Container
        const noResultsMsg = container.querySelector('#search-no-results-message');
        const itemsContainer = container.querySelector('#items-list-container');
        const queryDisplay = container.querySelector('#search-no-results-query');

        if (norm && totalMatches === 0) {
          if (itemsContainer) itemsContainer.style.display = 'none';
          if (noResultsMsg) {
            noResultsMsg.style.display = 'block';
            if (queryDisplay) queryDisplay.textContent = q;
          }
        } else {
          if (itemsContainer) itemsContainer.style.display = '';
          if (noResultsMsg) noResultsMsg.style.display = 'none';
        }
      };

      window._listaApplySearchFilter = applySearchFilter;

      searchInput.addEventListener('input', (e) => {
        applySearchFilter(e.target.value);
      });

      if (btnClearSearch) {
        btnClearSearch.addEventListener('click', () => {
          searchInput.value = '';
          applySearchFilter('');
          searchInput.focus();
        });
      }

      // Re-apply if returning from another action with an active query
      if (currentSearchQuery) {
        applySearchFilter(currentSearchQuery);
      }
    }

    // Back button
    const backBtn = container.querySelector('#btn-back');
    if (backBtn) {
      backBtn.addEventListener('click', () => {
        window.location.hash = '#listas';
      });
    }

    // Unlink Nota from List
    container.querySelectorAll('.btn-unlink-nota-from-list').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        e.stopPropagation();
        const notaId = btn.getAttribute('data-nota-id');
        btn.disabled = true;
        await unlinkNotaFromShoppingList(id, notaId);
        syncManager.showToast('Cupom fiscal desvinculado da lista.', 'info');
        await loadData();
        render();
      });
    });

    // Toggle Chat Collapsed / Expanded
    const chatToggleHeader = container.querySelector('#chat-toggle-header');
    const dragHandle = container.querySelector('#chat-drag-handle');

    // Drag-to-resize logic across the entire header bar
    if (chatToggleHeader) {
      let isDragging = false;
      let hasMoved = false;
      let startY = 0;
      let startHeight = chatHeight;

      const onPointerDown = (e) => {
        if (e.target.closest('#btn-toggle-chat')) return;
        isDragging = true;
        hasMoved = false;
        startY = e.clientY || (e.touches && e.touches[0].clientY);
        startHeight = isChatCollapsed ? 60 : chatHeight;
        chatToggleHeader.setPointerCapture?.(e.pointerId);
        document.body.style.userSelect = 'none';
      };

      const onPointerMove = (e) => {
        if (!isDragging) return;
        const currentY = e.clientY || (e.touches && e.touches[0].clientY);
        if (currentY === undefined) return;
        const deltaY = startY - currentY; // Moving UP increases height
        if (Math.abs(deltaY) > 5) {
          hasMoved = true;
        }

        const targetHeight = Math.max(120, Math.min(window.innerHeight * 0.75, startHeight + deltaY));

        if (isChatCollapsed && deltaY > 25) {
          isChatCollapsed = false;
          chatHeight = Math.max(240, targetHeight);
          render();
          return;
        }

        const bodyEl = container.querySelector('#chat-expanded-body');
        const wrapperEl = container.querySelector('#lista-content-wrapper');
        if (bodyEl) {
          bodyEl.style.height = `${targetHeight}px`;
        }
        if (wrapperEl) {
          wrapperEl.style.paddingBottom = `${targetHeight + 70}px`;
        }
      };

      const onPointerUp = (e) => {
        if (!isDragging) return;
        isDragging = false;
        document.body.style.userSelect = '';
        chatToggleHeader.releasePointerCapture?.(e.pointerId);

        const currentY = e.clientY || (e.changedTouches && e.changedTouches[0].clientY);
        if (currentY !== undefined) {
          const deltaY = startY - currentY;
          
          // If was a simple click/tap without moving -> toggle open/close
          if (!hasMoved && Math.abs(deltaY) < 6) {
            isChatCollapsed = !isChatCollapsed;
            render();
            if (!isChatCollapsed) {
              const stream = container.querySelector('#chat-messages-stream');
              if (stream) stream.scrollTop = stream.scrollHeight;
              container.querySelector('#input-chat-msg')?.focus();
            }
            return;
          }

          const finalHeight = startHeight + deltaY;
          if (finalHeight < 170) {
            isChatCollapsed = true;
            render();
          } else {
            isChatCollapsed = false;
            chatHeight = Math.max(200, Math.min(window.innerHeight * 0.75, finalHeight));
            render();
          }
        }
      };

      chatToggleHeader.addEventListener('pointerdown', onPointerDown);
      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', onPointerUp);
    }

    // Collapsed Chat Pill Click -> Open Chat
    const btnOpenChatPill = container.querySelector('#btn-open-chat-pill');
    if (btnOpenChatPill) {
      btnOpenChatPill.addEventListener('click', (e) => {
        e.stopPropagation();
        isChatCollapsed = false;
        render();
        const stream = container.querySelector('#chat-messages-stream');
        if (stream) stream.scrollTop = stream.scrollHeight;
        container.querySelector('#input-chat-msg')?.focus();
      });
    }

    // Toggle button click (when expanded)
    const btnToggleChat = container.querySelector('#btn-toggle-chat');
    if (btnToggleChat) {
      btnToggleChat.addEventListener('click', (e) => {
        e.stopPropagation();
        isChatCollapsed = !isChatCollapsed;
        render();
        if (!isChatCollapsed) {
          const stream = container.querySelector('#chat-messages-stream');
          if (stream) stream.scrollTop = stream.scrollHeight;
          container.querySelector('#input-chat-msg')?.focus();
        }
      });
    }

    // Cancel Tagged Item Quote
    const btnCancelQuote = container.querySelector('#btn-cancel-quote');
    if (btnCancelQuote) {
      btnCancelQuote.addEventListener('click', (e) => {
        e.stopPropagation();
        quotedItem = null;
        render();
      });
    }

    // Cancel Reply
    const btnCancelReply = container.querySelector('#btn-cancel-reply');
    if (btnCancelReply) {
      btnCancelReply.addEventListener('click', (e) => {
        e.stopPropagation();
        replyToMessage = null;
        render();
      });
    }

    // Tag / Quote Item from List
    container.querySelectorAll('.btn-tag-item').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const itemId = Number(btn.getAttribute('data-item-id'));
        const itemDesc = btn.getAttribute('data-item-desc') || 'Item';
        quotedItem = { id: itemId, description: itemDesc };
        isChatCollapsed = false;
        render();
        const stream = container.querySelector('#chat-messages-stream');
        if (stream) stream.scrollTop = stream.scrollHeight;
        const msgInp = container.querySelector('#input-chat-msg');
        if (msgInp) msgInp.focus();
      });
    });

    // Reply to a message in the chat
    container.querySelectorAll('.btn-reply-msg').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const msgId = Number(btn.getAttribute('data-msg-id'));
        const userName = btn.getAttribute('data-user') || 'Membro';
        const text = btn.getAttribute('data-text') || '';
        replyToMessage = { id: msgId, userName, text };
        isChatCollapsed = false;
        render();
        const stream = container.querySelector('#chat-messages-stream');
        if (stream) stream.scrollTop = stream.scrollHeight;
        const msgInp = container.querySelector('#input-chat-msg');
        if (msgInp) msgInp.focus();
      });
    });

    // Fechamento modal triggers
    const closeListBtn = container.querySelector('#btn-fechamento');
    const fechamentoModal = container.querySelector('#fechamento-lista-modal');
    const btnConfirmFechamento = container.querySelector('#btn-confirm-fechamento-lista');
    const btnCancelFechamento = container.querySelector('#btn-cancel-fechamento-lista');

    if (closeListBtn && fechamentoModal) {
      closeListBtn.addEventListener('click', (e) => {
        e.preventDefault();
        
        // Reset checkbox state
        const chk = container.querySelector('#checkbox-move-pending');
        const nameContainer = container.querySelector('#new-lista-name-container');
        const nameInput = container.querySelector('#input-new-lista-name');
        
        const pendingCount = (lista.items || []).filter(i => !i.isChecked && !i.is_checked).length;
        
        if (chk) {
          // Se existirem itens pendentes, ativa a opção por padrão para agilizar o fluxo
          chk.checked = pendingCount > 0;
          if (nameContainer) nameContainer.style.display = pendingCount > 0 ? 'block' : 'none';
          if (nameInput) nameInput.value = (lista.name || lista.nome || 'Lista') + ' (Continuação)';
          
          chk.onchange = (e) => {
            if (nameContainer) nameContainer.style.display = e.target.checked ? 'block' : 'none';
          };
        }
        
        fechamentoModal.style.setProperty('display', 'flex', 'important');
        fechamentoModal.classList.remove('hidden');
      });

      btnCancelFechamento?.addEventListener('click', () => {
        fechamentoModal.style.setProperty('display', 'none', 'important');
        fechamentoModal.classList.add('hidden');
      });

      fechamentoModal.addEventListener('click', (e) => {
        if (e.target === fechamentoModal) {
          fechamentoModal.style.setProperty('display', 'none', 'important');
          fechamentoModal.classList.add('hidden');
        }
      });

      btnConfirmFechamento?.addEventListener('click', async () => {
        fechamentoModal.style.setProperty('display', 'none', 'important');
        fechamentoModal.classList.add('hidden');
        
        const chk = container.querySelector('#checkbox-move-pending');
        const movePending = chk && chk.checked;
        const newNameInput = container.querySelector('#input-new-lista-name');
        const newName = newNameInput ? newNameInput.value.trim() : (lista.name || 'Nova Lista');
        
        let finalNewListId = null;
        let movedCount = 0;

        if (movePending && lista.items) {
          const pendingItems = lista.items.filter(i => !i.isChecked && !i.is_checked);
          if (pendingItems.length > 0) {
            movedCount = pendingItems.length;
            const targetScope = lista.scope || lista.escopo || getSavedScope() || 'personal';
            const targetType = lista.type || lista.tipo || 'semanal';
            const targetMonthRef = lista.monthRef || lista.mesRef || new Date().toISOString().slice(0, 7);

            // Crio nova lista
            const newList = {
              name: newName,
              type: targetType,
              scope: targetScope,
              status: 'aberta',
              monthRef: targetMonthRef,
              totalEstimated: 0,
              totalActual: 0,
            };
            
            if (authManager.isAuthenticated()) {
               try {
                 const res = await authManager.fetchWithAuth('/api/listas', {
                   method: 'POST',
                   headers: { 'Content-Type': 'application/json' },
                   body: JSON.stringify(newList),
                 });
                 if (res.ok) {
                   const created = await res.json();
                   await saveListaLocal(created);
                   finalNewListId = created.id;
                 } else {
                   const errText = await res.text().catch(() => '');
                   console.error('[Fechamento] Erro na API ao criar lista:', res.status, errText);
                 }
               } catch (e) {
                 console.warn('[Fechamento] Falha ao criar lista na nuvem, fallback local:', e);
               }
            }
            
            if (!finalNewListId) {
               const savedNewList = await saveListaLocal(newList);
               finalNewListId = savedNewList.id;
            }

            setSavedScope(targetScope);
            
            // Move itens para a nova lista
            for (const item of pendingItems) {
               const newItemPayload = {
                 listId: finalNewListId,
                 description: item.description || item.descricao || 'Produto',
                 categoryName: item.categoryName || item.categoriaNome || item.categoria || 'Outros',
                 quantity: Number(item.quantity || item.quantidade || 1),
                 unit: item.unit || item.unidade || 'UN',
                 estimatedPrice: Number(item.estimatedPrice || item.precoEstimado || 0),
                 actualPrice: 0,
                 actualQuantity: 0,
                 isChecked: false,
                 barcode: item.barcode || item.codigo || '',
               };
               
               let finalItem = null;
               if (authManager.isAuthenticated() && typeof finalNewListId === 'number' && finalNewListId < 1000000000000) {
                 try {
                   const resItem = await authManager.fetchWithAuth(`/api/listas/${finalNewListId}/itens`, {
                     method: 'POST',
                     headers: { 'Content-Type': 'application/json' },
                     body: JSON.stringify(newItemPayload),
                   });
                   if (resItem.ok) {
                     finalItem = await resItem.json();
                   }
                 } catch (e) {
                   console.warn('[Fechamento] Falha ao enviar item para nova lista na nuvem:', e);
                 }
               }
               
               if (!finalItem) {
                 finalItem = { ...newItemPayload, id: Date.now() + Math.floor(Math.random() * 1000) };
               }
               
               await saveListaItemLocal(finalItem);
               
               // Remove da lista antiga
               if (authManager.isAuthenticated() && item.id) {
                 authManager.fetchWithAuth(`/api/listas/${lista.id}/itens/${item.id}`, { method: 'DELETE' }).catch(() => {});
               }
               await deleteListaItemLocal(item.id, lista.id);
            }
          }
        }

        // Fecha a lista atual
        if (authManager.isAuthenticated()) {
          await authManager.fetchWithAuth(`/api/listas/${lista.id}/fechamento`, { method: 'POST' }).catch(() => {});
        }
        await closeListaLocal(lista.id);
        
        // Se criou uma nova lista continuação, navega diretamente para ela para o usuário ver na hora!
        if (finalNewListId) {
          syncManager?.showToast(`Lista fechada! ${movedCount} itens movidos para: ${newName}`, 'success');
          window.location.hash = `#lista/${finalNewListId}`;
          return;
        }

        syncManager?.showToast('Fechamento da lista realizado com sucesso!', 'success');
        await loadData(true);
        render();
      });
    }

    // Delete list modal trigger
    const deleteListBtn = container.querySelector('#btn-delete-lista');
    const deleteModal = container.querySelector('#delete-lista-modal');
    const btnConfirmDelete = container.querySelector('#btn-confirm-delete-lista');
    const btnCancelDelete = container.querySelector('#btn-cancel-delete-lista');

    if (deleteListBtn && deleteModal) {
      deleteListBtn.addEventListener('click', (e) => {
        e.preventDefault();
        deleteModal.style.setProperty('display', 'flex', 'important');
        deleteModal.classList.remove('hidden');
      });

      btnCancelDelete?.addEventListener('click', () => {
        deleteModal.style.setProperty('display', 'none', 'important');
        deleteModal.classList.add('hidden');
      });

      deleteModal.addEventListener('click', (e) => {
        if (e.target === deleteModal) {
          deleteModal.style.setProperty('display', 'none', 'important');
          deleteModal.classList.add('hidden');
        }
      });

      btnConfirmDelete?.addEventListener('click', async () => {
        deleteModal.style.setProperty('display', 'none', 'important');
        deleteModal.classList.add('hidden');
        try {
          if (authManager.isAuthenticated()) {
            await authManager.fetchWithAuth(`/api/listas/${lista.id}`, { method: 'DELETE' }).catch(() => {});
          }
          await deleteListaLocal(lista.id);
        } catch (err) {
          console.warn('Delete lista error:', err);
        }
        window.location.hash = '#listas';
      });
    }

    // Live search & Autocomplete on input-manual-desc
    const manualDescInput = container.querySelector('#input-manual-desc');
    const suggestionsBox = container.querySelector('#product-search-suggestions');
    const btnClearDesc = container.querySelector('#btn-clear-manual-desc');

    const updateClearBtnState = () => {
      if (btnClearDesc && manualDescInput) {
        btnClearDesc.style.display = manualDescInput.value.trim().length > 0 ? 'inline-flex' : 'none';
      }
    };

    if (manualDescInput && suggestionsBox) {
      manualDescInput.addEventListener('input', async () => {
        updateClearBtnState();
        const q = manualDescInput.value.trim();
        if (q.length < 2) {
          suggestionsBox.style.display = 'none';
          suggestionsBox.innerHTML = '';
          return;
        }

        const results = await searchHistoricalProducts(q);
        if (results.length === 0) {
          suggestionsBox.style.display = 'none';
          return;
        }

        suggestionsBox.innerHTML = results.map(r => `
          <div class="product-sugg-item" data-desc="${escapeHTML(r.description)}" data-cat="${escapeHTML(r.categoryName)}" data-unit="${escapeHTML(r.unit)}" data-price="${r.estimatedPrice.toFixed(2)}" style="padding: 0.65rem 0.95rem; border-bottom: 1px solid var(--border-color-subtle, #F1F5F9); cursor: pointer; display: flex; justify-content: space-between; align-items: center;">
            <div>
              <div style="font-size: 0.875rem; font-weight: 600; color: var(--text-main);">${escapeHTML(r.description)}</div>
              <div class="text-muted" style="font-size: 0.72rem;">${escapeHTML(r.categoryName)} &bull; ${escapeHTML(r.store || 'Histórico')}</div>
            </div>
            <div class="font-mono font-bold" style="font-size: 0.85rem; color: #10B981;">R$ ${r.estimatedPrice.toFixed(2).replace('.', ',')}</div>
          </div>
        `).join('');
        suggestionsBox.style.display = 'block';

        suggestionsBox.querySelectorAll('.product-sugg-item').forEach(el => {
          el.addEventListener('click', () => {
            const desc = el.getAttribute('data-desc');
            const price = el.getAttribute('data-price');

            manualDescInput.value = desc;
            updateClearBtnState();
            const priceInp = container.querySelector('#input-manual-price');
            if (priceInp && price && parseFloat(price) > 0) {
              priceInp.value = price;
            }

            suggestionsBox.style.display = 'none';
          });
        });
      });

      if (btnClearDesc) {
        btnClearDesc.addEventListener('click', () => {
          manualDescInput.value = '';
          updateClearBtnState();
          suggestionsBox.style.display = 'none';
          suggestionsBox.innerHTML = '';
          manualDescInput.focus();
        });
      }

      // Close suggestions on click outside
      document.addEventListener('click', (evt) => {
        if (!manualDescInput.contains(evt.target) && !suggestionsBox.contains(evt.target)) {
          suggestionsBox.style.display = 'none';
        }
      });
    }

    // Stepper controls for Manual Add Item
    const btnManualMinus = container.querySelector('#btn-manual-qty-minus');
    const btnManualPlus = container.querySelector('#btn-manual-qty-plus');
    const inputManualQty = container.querySelector('#input-manual-qty');

    if (btnManualMinus && inputManualQty) {
      btnManualMinus.addEventListener('click', () => {
        let currentVal = parseFloat(inputManualQty.value) || 1;
        if (currentVal > 1) {
          currentVal = Math.max(1, currentVal - 1);
        } else if (currentVal > 0.5) {
          currentVal = 0.5;
        } else if (currentVal > 0.1) {
          currentVal = Math.max(0.1, Number((currentVal - 0.1).toFixed(1)));
        }
        inputManualQty.value = currentVal;
      });
    }

    if (btnManualPlus && inputManualQty) {
      btnManualPlus.addEventListener('click', () => {
        let currentVal = parseFloat(inputManualQty.value) || 0;
        if (currentVal < 1) {
          currentVal = 1;
        } else {
          currentVal = Math.floor(currentVal) + 1;
        }
        inputManualQty.value = currentVal;
      });
    }

    container.querySelectorAll('.btn-manual-qty-preset').forEach(btn => {
      btn.addEventListener('click', () => {
        if (!inputManualQty) return;
        const delta = btn.getAttribute('data-delta');
        const setVal = btn.getAttribute('data-set');
        if (delta) {
          const add = parseFloat(delta) || 0;
          let currentVal = parseFloat(inputManualQty.value) || 0;
          inputManualQty.value = Math.max(1, currentVal + add);
        } else if (setVal) {
          inputManualQty.value = setVal;
        }
      });
    });

    // Manual item form
    const addItemForm = container.querySelector('#form-quick-add-item');
    if (addItemForm) {
      let isSubmitting = false;
      addItemForm.addEventListener('submit', async (e) => {
        e.preventDefault();
        if (isSubmitting) return;

        const desc = container.querySelector('#input-manual-desc')?.value.trim();
        const qty = parseFloat(container.querySelector('#input-manual-qty')?.value || '1');
        const price = parseFloat(container.querySelector('#input-manual-price')?.value || '0');

        if (!desc) return;

        isSubmitting = true;
        const submitBtn = addItemForm.querySelector('button[type="submit"]');
        if (submitBtn) submitBtn.disabled = true;

        const catDetected = categorizeItem(desc);
        const categoryName = catDetected?.nome || 'Outros';

        const tempId = Date.now();
        const itemPayload = {
          id: tempId,
          listId: lista.id,
          description: desc,
          categoryName: categoryName,
          quantity: qty,
          unit: 'UN',
          estimatedPrice: price,
          actualPrice: 0,
          isChecked: false,
        };

        // 1. Optimistic Local Save & Render
        lastLocalMutationTime = Date.now();
        await saveListaItemLocal(itemPayload);
        if (lista && Array.isArray(lista.items)) {
          lista.items.push(itemPayload);
        }
        render();

        if (syncManager?.showToast) {
          syncManager.showToast('Item adicionado à lista!', 'success');
        }

        // Reset form immediately
        const descInp = container.querySelector('#input-manual-desc');
        const qtyInp = container.querySelector('#input-manual-qty');
        const priceInp = container.querySelector('#input-manual-price');
        const clearDescBtn = container.querySelector('#btn-clear-manual-desc');
        const suggBox = container.querySelector('#product-search-suggestions');
        if (descInp) descInp.value = '';
        if (qtyInp) qtyInp.value = '1';
        if (priceInp) priceInp.value = '';
        if (clearDescBtn) clearDescBtn.style.display = 'none';
        if (suggBox) suggBox.style.display = 'none';
        const searchInputEl = container.querySelector('#search-list-items-input');
        if (document.activeElement !== searchInputEl && descInp) {
          descInp.focus();
        }

        setTimeout(() => {
          isSubmitting = false;
          if (submitBtn) submitBtn.disabled = false;
        }, 300);

        // 2. Background Sync
        if (authManager.isAuthenticated()) {
          authManager.fetchWithAuth(`/api/listas/${lista.id}/itens`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(itemPayload),
          }).then(async res => {
            if (res.ok) {
              const created = await res.json();
              if (created && created.id) {
                // Replace temp item with created item in memory & IndexedDB
                if (lista && Array.isArray(lista.items)) {
                  const idx = lista.items.findIndex(i => i.id === tempId);
                  if (idx > -1) {
                    lista.items[idx] = created;
                  }
                }
                await deleteListaItemLocal(tempId, lista.id).catch(() => {});
                await saveListaItemLocal(created);

                // Update DOM attribute if element is present without full refresh
                const rowWrapper = container.querySelector(`.swipe-item-wrapper[data-item-id="${tempId}"]`);
                if (rowWrapper) {
                  rowWrapper.setAttribute('data-item-id', created.id);
                  const row = rowWrapper.querySelector('.lista-item-row-compact');
                  if (row) row.setAttribute('data-item-id', created.id);
                  const chk = rowWrapper.querySelector('.item-checkbox');
                  if (chk) chk.setAttribute('data-item-id', created.id);
                  const btnQty = rowWrapper.querySelector('.btn-item-qty-chip');
                  if (btnQty) btnQty.setAttribute('data-item-id', created.id);
                  const btnDel = rowWrapper.querySelector('.btn-delete-item');
                  if (btnDel) btnDel.setAttribute('data-item-id', created.id);
                }
              }
            }
          }).catch(err => console.warn('Background sync failed', err));
        }
      });
    }

    // === Confirm Buy / Actual Quantity Modal & Long Press Logic ===
    const buyModal = container.querySelector('#modal-confirm-buy');
    let selectedBuyItem = null;

    // Currency Mask Helpers (Available across all modal handlers)
    const formatCurrencyInput = (val) => {
      const cleanDigits = (val || '').toString().replace(/\D/g, '');
      if (!cleanDigits) return '';
      const numeric = parseInt(cleanDigits, 10) / 100;
      return numeric.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const parseCurrencyInput = (val) => {
      if (!val) return 0;
      const clean = val.replace(/\./g, '').replace(',', '.').replace(/[^\d.]/g, '');
      const num = parseFloat(clean);
      return isNaN(num) ? 0 : num;
    };

    const closeBuyModal = () => {
      if (buyModal) {
        buyModal.classList.add('hidden');
      }
      selectedBuyItem = null;
    };

    openBuyModal = (item) => {
      if (isClosed || !item) return;
      selectedBuyItem = item;
      const rawDesc = item.description || item.descricao || 'Produto';
      const itemDesc = cleanProductDescription(rawDesc) || rawDesc;
      const qty = item.quantity || item.quantidade || 1;
      const actQty = item.actualQuantity || item.quantidadeComprada || 0;
      const est = item.estimatedPrice || item.precoEstimado || 0;
      const actPrice = item.actualPrice || item.precoReal || 0;
      const unit = item.unit || item.unidade || 'UN';

      const descEl = container.querySelector('#buy-modal-desc');
      const plannedEl = container.querySelector('#buy-modal-planned');
      const estEl = container.querySelector('#buy-modal-est-price');
      const qtyInput = container.querySelector('#input-buy-qty');
      const priceInput = container.querySelector('#input-buy-price');
      const uncheckBtn = container.querySelector('#btn-uncheck-item');
      const presetAllBtn = container.querySelector('#btn-preset-all');
      const presetHalfBtn = container.querySelector('#btn-preset-half');

      // Edit Mode Elements
      const editQtyInput = container.querySelector('#input-edit-planned-qty');
      const editPriceInput = container.querySelector('#input-edit-estimated-price');

      if (descEl) descEl.textContent = itemDesc;
      if (plannedEl) plannedEl.textContent = `${qty} ${unit}`;
      if (estEl) estEl.textContent = est > 0 ? `R$ ${est.toFixed(2).replace('.', ',')} un` : 'Sem estimativa';

      // Default to actualQuantity if previously set, otherwise planned qty
      if (qtyInput) {
        qtyInput.value = actQty > 0 ? actQty : qty;
      }
      if (priceInput) {
        if (actPrice > 0) {
          priceInput.value = formatCurrencyInput(Math.round(actPrice * 100).toString());
        } else if (est > 0) {
          priceInput.value = formatCurrencyInput(Math.round(est * 100).toString());
        } else {
          priceInput.value = '';
        }
      }

      // Populate Edit Mode inputs
      if (editQtyInput) {
        editQtyInput.value = qty;
      }
      if (editPriceInput) {
        editPriceInput.value = est > 0 ? formatCurrencyInput(Math.round(est * 100).toString()) : '';
      }

      // Dynamic Smart Quantity Presets (Tudo, 2, 3, etc. and Não Encontrei)
      const presetsContainer = container.querySelector('#buy-modal-presets-container');
      if (presetsContainer) {
        const suggestions = [];
        suggestions.push({ label: `Tudo (${qty})`, qty });

        if (qty === 1) {
          suggestions.push({ label: '2 un', qty: 2 });
          suggestions.push({ label: '3 un', qty: 3 });
        } else if (qty === 2) {
          suggestions.push({ label: '1 un', qty: 1 });
          suggestions.push({ label: '3 un', qty: 3 });
        } else if (qty === 3) {
          suggestions.push({ label: '2 un', qty: 2 });
          suggestions.push({ label: '1 un', qty: 1 });
        } else if (qty === 4) {
          suggestions.push({ label: '3 un', qty: 3 });
          suggestions.push({ label: '2 un', qty: 2 });
        } else {
          const lower1 = Math.max(1, qty - 1);
          const lower2 = Math.max(1, Math.floor(qty / 2));
          if (lower2 !== qty && lower2 !== lower1) suggestions.push({ label: `${lower2} un`, qty: lower2 });
          if (lower1 !== qty) suggestions.push({ label: `${lower1} un`, qty: lower1 });
          suggestions.push({ label: '1 un', qty: 1 });
        }

        presetsContainer.innerHTML = `
          ${suggestions.map(s => `
            <button type="button" class="btn-buy-preset" data-qty="${s.qty}" style="background: var(--bg-surface-dim); border: 1px solid var(--border-color); color: var(--text-main); border-radius: var(--radius-pill); font-size: 0.76rem; font-weight: 700; padding: 0.28rem 0.7rem; cursor: pointer; transition: all 0.15s ease;">
              ${s.label}
            </button>
          `).join('')}
          <button type="button" class="btn-buy-preset btn-preset-zero" data-qty="0" style="background: rgba(239, 68, 68, 0.08); border: 1px solid rgba(239, 68, 68, 0.3); color: var(--color-error, #EF4444); border-radius: var(--radius-pill); font-size: 0.76rem; font-weight: 700; padding: 0.28rem 0.7rem; cursor: pointer; transition: all 0.15s ease;">
            Não Encontrei (0)
          </button>
        `;

        presetsContainer.querySelectorAll('.btn-buy-preset').forEach(btn => {
          btn.addEventListener('click', () => {
            const q = parseFloat(btn.getAttribute('data-qty'));
            if (qtyInput && !isNaN(q)) {
              qtyInput.value = q;
            }
          });
        });
      }

      const isChecked = Boolean(item.isChecked || item.is_checked);
      if (uncheckBtn) {
        uncheckBtn.style.display = isChecked ? 'block' : 'none';
      }

      // Always reset to 'buy' mode tab on open
      setBuyModalMode('buy');

      if (buyModal) {
        buyModal.classList.remove('hidden');
      }
    };

    const closeBuyModalBtn = container.querySelector('#btn-close-buy-modal');
    if (closeBuyModalBtn) closeBuyModalBtn.addEventListener('click', closeBuyModal);

    // Modal Mode Tab Switcher Logic
    function setBuyModalMode(mode) {
      const tabBuy = container.querySelector('#tab-mode-buy');
      const tabEdit = container.querySelector('#tab-mode-edit');
      const formBuy = container.querySelector('#form-confirm-buy');
      const formEdit = container.querySelector('#form-edit-list-item');
      const headerIcon = container.querySelector('#buy-modal-header-icon');
      const headerTitle = container.querySelector('#buy-modal-title');

      if (mode === 'edit') {
        if (tabBuy) {
          tabBuy.style.background = 'transparent';
          tabBuy.style.color = 'var(--text-muted, #64748B)';
          tabBuy.style.boxShadow = 'none';
        }
        if (tabEdit) {
          tabEdit.style.background = 'var(--text-main, #0F172A)';
          tabEdit.style.color = '#FFF';
          tabEdit.style.boxShadow = '0 1px 3px rgba(0,0,0,0.15)';
        }
        if (formBuy) formBuy.style.display = 'none';
        if (formEdit) formEdit.style.display = 'block';
        if (headerIcon) headerIcon.textContent = 'edit_note';
        if (headerTitle) headerTitle.textContent = 'Editar Item na Lista';
      } else {
        if (tabEdit) {
          tabEdit.style.background = 'transparent';
          tabEdit.style.color = 'var(--text-muted, #64748B)';
          tabEdit.style.boxShadow = 'none';
        }
        if (tabBuy) {
          tabBuy.style.background = 'var(--text-main, #0F172A)';
          tabBuy.style.color = '#FFF';
          tabBuy.style.boxShadow = '0 1px 3px rgba(0,0,0,0.15)';
        }
        if (formEdit) formEdit.style.display = 'none';
        if (formBuy) formBuy.style.display = 'block';
        if (headerIcon) headerIcon.textContent = 'shopping_cart_checkout';
        if (headerTitle) headerTitle.textContent = 'Confirmar no Carrinho';
      }
    }

    container.querySelector('#tab-mode-buy')?.addEventListener('click', () => setBuyModalMode('buy'));
    container.querySelector('#tab-mode-edit')?.addEventListener('click', () => setBuyModalMode('edit'));
    container.querySelector('#btn-cancel-edit-item')?.addEventListener('click', closeBuyModal);

    // Live Currency Mask on Price Inputs
    const priceInputEl = container.querySelector('#input-buy-price');
    if (priceInputEl) {
      priceInputEl.addEventListener('input', (e) => {
        const cleanDigits = (e.target.value || '').replace(/\D/g, '');
        if (!cleanDigits) {
          e.target.value = '';
          return;
        }
        const numeric = parseInt(cleanDigits, 10) / 100;
        e.target.value = numeric.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      });
    }

    const editPriceInputEl = container.querySelector('#input-edit-estimated-price');
    if (editPriceInputEl) {
      editPriceInputEl.addEventListener('input', (e) => {
        const cleanDigits = (e.target.value || '').replace(/\D/g, '');
        if (!cleanDigits) {
          e.target.value = '';
          return;
        }
        const numeric = parseInt(cleanDigits, 10) / 100;
        e.target.value = numeric.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      });
    }

    // Buy Stepper buttons & Presets
    const btnQtyMinus = container.querySelector('#btn-buy-qty-minus');
    const btnQtyPlus = container.querySelector('#btn-buy-qty-plus');
    const qtyInput = container.querySelector('#input-buy-qty');

    if (btnQtyMinus && qtyInput) {
      btnQtyMinus.addEventListener('click', () => {
        let val = parseFloat(qtyInput.value) || 0;
        val = Math.max(0, val - 1);
        qtyInput.value = val;
      });
    }

    if (btnQtyPlus && qtyInput) {
      btnQtyPlus.addEventListener('click', () => {
        let val = parseFloat(qtyInput.value) || 0;
        val += 1;
        qtyInput.value = val;
      });
    }

    const presetAll = container.querySelector('#btn-preset-all');
    const presetHalf = container.querySelector('#btn-preset-half');
    const presetZero = container.querySelector('#btn-preset-zero');

    if (presetAll && qtyInput) {
      presetAll.addEventListener('click', () => {
        if (selectedBuyItem) {
          qtyInput.value = selectedBuyItem.quantity || selectedBuyItem.quantidade || 1;
        }
      });
    }

    if (presetHalf && qtyInput) {
      presetHalf.addEventListener('click', () => {
        if (selectedBuyItem) {
          const q = selectedBuyItem.quantity || selectedBuyItem.quantidade || 1;
          qtyInput.value = q > 1 ? (q / 2) : 1;
        }
      });
    }

    if (presetZero && qtyInput) {
      presetZero.addEventListener('click', () => {
        qtyInput.value = 0;
      });
    }

    // Edit Stepper buttons & Presets
    const btnEditQtyMinus = container.querySelector('#btn-edit-qty-minus');
    const btnEditQtyPlus = container.querySelector('#btn-edit-qty-plus');
    const editQtyInputEl = container.querySelector('#input-edit-planned-qty');

    if (btnEditQtyMinus && editQtyInputEl) {
      btnEditQtyMinus.addEventListener('click', () => {
        let val = parseFloat(editQtyInputEl.value) || 1;
        if (val > 1) {
          val = Math.max(1, val - 1);
        } else if (val > 0.5) {
          val = 0.5;
        } else if (val > 0.1) {
          val = Math.max(0.1, Number((val - 0.1).toFixed(1)));
        }
        editQtyInputEl.value = val;
      });
    }

    if (btnEditQtyPlus && editQtyInputEl) {
      btnEditQtyPlus.addEventListener('click', () => {
        let val = parseFloat(editQtyInputEl.value) || 0;
        if (val < 1) {
          val = 1;
        } else {
          val = Math.floor(val) + 1;
        }
        editQtyInputEl.value = val;
      });
    }

    container.querySelectorAll('.btn-edit-preset').forEach(btn => {
      btn.addEventListener('click', () => {
        if (!editQtyInputEl) return;
        const delta = btn.getAttribute('data-delta');
        const setVal = btn.getAttribute('data-set');
        if (delta) {
          const add = parseFloat(delta) || 0;
          let currentVal = parseFloat(editQtyInputEl.value) || 0;
          editQtyInputEl.value = Math.max(1, currentVal + add);
        } else if (setVal) {
          editQtyInputEl.value = setVal;
        }
      });
    });

    // Form Submit: Edit List Item (Updates planned quantity & estimated price without marking bought)
    const formEditListItem = container.querySelector('#form-edit-list-item');
    if (formEditListItem) {
      formEditListItem.addEventListener('submit', async (e) => {
        e.preventDefault();
        if (!selectedBuyItem) return;

        const itemId = selectedBuyItem.id;
        const newPlannedQty = parseFloat(container.querySelector('#input-edit-planned-qty')?.value || '1');
        const rawEstPriceStr = container.querySelector('#input-edit-estimated-price')?.value || '';
        const newEstPrice = parseCurrencyInput(rawEstPriceStr);

        const updatedPayload = {
          ...selectedBuyItem,
          quantity: Math.max(0.1, newPlannedQty),
          estimatedPrice: newEstPrice,
        };

        if (authManager.isAuthenticated()) {
          try {
            await authManager.fetchWithAuth(`/api/listas/${lista.id}/itens/${itemId}`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(updatedPayload),
            });
          } catch (err) {
            console.warn('Erro ao atualizar item na API:', err);
          }
        }

        await saveListaItemLocal(updatedPayload);
        closeBuyModal();
        if (syncManager?.showToast) {
          syncManager.showToast('Item atualizado na lista!', 'success');
        }
        await loadData();
        render();
      });
    }

    // Uncheck action from modal
    const uncheckBtn = container.querySelector('#btn-uncheck-item');
    if (uncheckBtn) {
      uncheckBtn.addEventListener('click', async () => {
        if (!selectedBuyItem) return;
        lastLocalMutationTime = Date.now();
        const itemId = selectedBuyItem.id;
        const updatedPayload = {
          ...selectedBuyItem,
          isChecked: false,
          actualQuantity: 0,
          actualPrice: 0,
        };

        // Optimistic update
        await toggleListaItemLocal(itemId, lista.id, false, 0, 0);
        const idx = (lista.items || []).findIndex(i => i.id === itemId);
        if (idx > -1) lista.items[idx] = updatedPayload;

        // Background sync
        if (authManager.isAuthenticated()) {
          authManager.fetchWithAuth(`/api/listas/${lista.id}/itens/${itemId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(updatedPayload),
          }).catch(() => {});
        }

        closeBuyModal();
        if (syncManager?.showToast) {
          syncManager.showToast('Item desmarcado', 'info');
        }
        render();
      });
    }

    // Form Submit: Save in Cart with Split Logic for Partial Purchases
    const formConfirmBuy = container.querySelector('#form-confirm-buy');
    if (formConfirmBuy) {
      formConfirmBuy.addEventListener('submit', async (e) => {
        e.preventDefault();
        if (!selectedBuyItem) return;

        lastLocalMutationTime = Date.now();
        const itemId = selectedBuyItem.id;
        const itemCat = selectedBuyItem.categoryName || selectedBuyItem.categoriaNome || 'Outros';
        const plannedQty = selectedBuyItem.quantity || selectedBuyItem.quantidade || 1;
        const boughtQty = parseFloat(container.querySelector('#input-buy-qty')?.value || '0');
        const rawPriceStr = container.querySelector('#input-buy-price')?.value || '';
        const realPrice = parseCurrencyInput(rawPriceStr);

        if (boughtQty <= 0) {
          // Zeroed / Not found
          const updatedPayload = {
            ...selectedBuyItem,
            isChecked: false,
            actualQuantity: 0,
            actualPrice: 0,
          };

          if (authManager.isAuthenticated()) {
            authManager.fetchWithAuth(`/api/listas/${lista.id}/itens/${itemId}`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(updatedPayload),
            }).catch(() => {});
          }

          await toggleListaItemLocal(itemId, lista.id, false, 0, 0);
          const idx = (lista.items || []).findIndex(i => i.id === itemId);
          if (idx > -1) lista.items[idx] = updatedPayload;

          closeBuyModal();
          if (syncManager?.showToast) {
            syncManager.showToast('Item marcado como não encontrado (0 un).', 'info');
          }
          render();
        } else if (boughtQty < plannedQty) {
          // === Compra Parcial: Dividir o item (Parte Comprada no Carrinho + Parte Restante Aberta) ===
          expandedComprados.add(itemCat);
          const remainingQty = plannedQty - boughtQty;

          // 1. Atualiza o item atual com a quantidade comprada e fecha no carrinho
          const boughtPayload = {
            ...selectedBuyItem,
            quantity: boughtQty,
            actualQuantity: boughtQty,
            actualPrice: realPrice,
            isChecked: true,
          };

          if (authManager.isAuthenticated()) {
            authManager.fetchWithAuth(`/api/listas/${lista.id}/itens/${itemId}`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(boughtPayload),
            }).catch(() => {});
          }
          await saveListaItemLocal(boughtPayload);
          await toggleListaItemLocal(itemId, lista.id, true, boughtQty, realPrice);
          const idx = (lista.items || []).findIndex(i => i.id === itemId);
          if (idx > -1) lista.items[idx] = boughtPayload;

          // 2. Cria automaticamente o item restante que permanece aberto / pendente na lista
          const remainingTempId = Date.now();
          const remainingPayload = {
            id: remainingTempId,
            listId: lista.id,
            description: selectedBuyItem.description || selectedBuyItem.descricao,
            quantity: remainingQty,
            unit: selectedBuyItem.unit || selectedBuyItem.unidade || 'UN',
            estimatedPrice: selectedBuyItem.estimatedPrice || selectedBuyItem.precoEstimado || 0,
            actualPrice: 0,
            categoryName: itemCat,
            isChecked: false,
            actualQuantity: 0,
          };

          if (lista && Array.isArray(lista.items)) {
            lista.items.push(remainingPayload);
          }
          await saveListaItemLocal(remainingPayload);

          if (authManager.isAuthenticated()) {
            authManager.fetchWithAuth(`/api/listas/${lista.id}/itens`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(remainingPayload),
            }).then(async (res) => {
              if (res.ok) {
                const created = await res.json();
                if (created && created.id && lista && Array.isArray(lista.items)) {
                  const remIdx = lista.items.findIndex(i => i.id === remainingTempId);
                  if (remIdx > -1) lista.items[remIdx] = created;
                  await deleteListaItemLocal(remainingTempId, lista.id).catch(() => {});
                  await saveListaItemLocal(created);
                }
              }
            }).catch(() => {});
          }

          closeBuyModal();
          if (syncManager?.showToast) {
            syncManager.showToast(`Item dividido: ${boughtQty} no carrinho e ${remainingQty} pendente!`, 'success');
          }
          render();
        } else {
          // === Compra Total (100% ou mais) ===
          expandedComprados.add(itemCat);
          const updatedPayload = {
            ...selectedBuyItem,
            quantity: boughtQty,
            actualQuantity: boughtQty,
            actualPrice: realPrice,
            isChecked: true,
          };

          // Optimistic local save
          await saveListaItemLocal(updatedPayload);
          await toggleListaItemLocal(itemId, lista.id, true, boughtQty, realPrice);
          const idx = (lista.items || []).findIndex(i => i.id === itemId);
          if (idx > -1) lista.items[idx] = updatedPayload;

          // Background sync
          if (authManager.isAuthenticated()) {
            authManager.fetchWithAuth(`/api/listas/${lista.id}/itens/${itemId}`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(updatedPayload),
            }).catch(() => {});
          }

          closeBuyModal();
          if (syncManager?.showToast) {
            syncManager.showToast('Item confirmado no carrinho!', 'success');
          }
          render();
        }
      });
    }

    // === Delete Item Confirmation Modal ===
    let itemToDelete = null;
    const deleteItemModal = container.querySelector('#delete-item-modal');
    const deleteItemDescEl = container.querySelector('#delete-item-modal-desc');
    const btnCancelDeleteItem = container.querySelector('#btn-cancel-delete-item');
    const btnConfirmDeleteItem = container.querySelector('#btn-confirm-delete-item');

    openDeleteItemModal = (item) => {
      if (isClosed || !item) return;
      itemToDelete = item;
      const rawDesc = item.description || item.descricao || 'Produto';
      const itemDesc = cleanProductDescription(rawDesc) || rawDesc;
      if (deleteItemDescEl) deleteItemDescEl.textContent = `"${itemDesc}"`;
      if (deleteItemModal) deleteItemModal.classList.remove('hidden');
    };

    const closeDeleteItemModal = () => {
      if (deleteItemModal) deleteItemModal.classList.add('hidden');
      itemToDelete = null;
    };

    if (btnCancelDeleteItem) btnCancelDeleteItem.addEventListener('click', closeDeleteItemModal);

    if (deleteItemModal) {
      deleteItemModal.addEventListener('click', (e) => {
        if (e.target === deleteItemModal) closeDeleteItemModal();
      });
    }

    if (btnConfirmDeleteItem) {
      btnConfirmDeleteItem.addEventListener('click', async () => {
        if (!itemToDelete) return;
        const itemId = itemToDelete.id;
        closeDeleteItemModal();

        lastLocalMutationTime = Date.now();
        if (lista && Array.isArray(lista.items)) {
          lista.items = lista.items.filter(i => i.id !== itemId);
        }
        await deleteListaItemLocal(itemId, lista.id);

        if (authManager.isAuthenticated()) {
          authManager.fetchWithAuth(`/api/listas/${lista.id}/itens/${itemId}`, { method: 'DELETE' }).catch(() => {});
        }
        if (syncManager?.showToast) {
          syncManager.showToast('Item removido da lista.', 'info');
        }
        render();
      });
    }

    bindItemRowEvents = () => {
      const currentItems = lista.items || [];
      const currentIsClosed = lista.status === 'fechada';

      // Completed Items Accordion Toggle (In-place DOM toggle without any re-render)
      container.querySelectorAll('.comprados-accordion-header').forEach(hdr => {
        hdr.onclick = (e) => {
          e.stopPropagation();
          const cat = hdr.getAttribute('data-cat');
          const body = hdr.parentElement?.querySelector('.comprados-accordion-body');
          const chevron = hdr.querySelector('.accordion-chevron') || hdr.querySelector('.material-symbols-outlined:last-child');
          if (expandedComprados.has(cat)) {
            expandedComprados.delete(cat);
            if (body) body.classList.add('hidden');
            if (chevron) chevron.style.transform = 'rotate(0deg)';
          } else {
            expandedComprados.add(cat);
            if (body) body.classList.remove('hidden');
            if (chevron) chevron.style.transform = 'rotate(180deg)';
          }
        };
      });

      // Direct Click on Qty Chip
      container.querySelectorAll('.btn-item-qty-chip').forEach(btn => {
        btn.onclick = (e) => {
          e.stopPropagation();
          const rawId = btn.getAttribute('data-item-id');
          const item = currentItems.find(it => String(it.id) === String(rawId));
          if (item) openBuyModal(item);
        };
      });

      // Swipe Gestures & Interactions on Item Rows
      container.querySelectorAll('.swipe-item-wrapper').forEach(wrapper => {
        const row = wrapper.querySelector('.lista-item-row-compact');
        if (!row) return;

        const rawId = wrapper.getAttribute('data-item-id');
        const item = currentItems.find(it => String(it.id) === String(rawId));
        if (!item) return;

        let startX = 0;
        let startY = 0;
        let currentDx = 0;
        let isSwiping = false;
        let isHorizontal = false;
        let isDecided = false;
        let lastClickTime = 0;
        let pressTimer = null;
        let isLongPressed = false;

        const bgLeft = wrapper.querySelector('.swipe-action-left');
        const bgRight = wrapper.querySelector('.swipe-action-right');

        const triggerModal = () => {
          openBuyModal(item);
        };

        const cancelPress = () => {
          if (pressTimer) {
            clearTimeout(pressTimer);
            pressTimer = null;
          }
        };

        const onStart = (clientX, clientY, target) => {
          if (currentIsClosed) return;
          if (target.closest('.item-checkbox') || target.closest('.btn-delete-item') || target.closest('.btn-tag-item') || target.closest('.btn-item-qty-chip')) {
            return;
          }
          startX = clientX;
          startY = clientY;
          currentDx = 0;
          isSwiping = true;
          isHorizontal = false;
          isDecided = false;
          isLongPressed = false;
          row.style.transition = 'none';

          pressTimer = setTimeout(() => {
            if (!isHorizontal && !currentIsClosed) {
              isLongPressed = true;
              triggerModal();
            }
          }, 450);
        };

        const onMove = (clientX, clientY) => {
          if (!isSwiping) return;
          const dx = clientX - startX;
          const dy = clientY - startY;

          if (!isDecided) {
            if (Math.abs(dx) > 6 || Math.abs(dy) > 6) {
              isDecided = true;
              if (Math.abs(dx) > Math.abs(dy)) {
                isHorizontal = true;
                cancelPress();
              } else {
                isHorizontal = false;
                isSwiping = false;
                cancelPress();
                return;
              }
            }
          }

          if (isHorizontal) {
            currentDx = dx;
            const displayDx = Math.sign(dx) * Math.min(100, Math.abs(dx));
            row.style.transform = `translateX(${displayDx}px)`;

            if (bgLeft && bgRight) {
              bgLeft.style.opacity = dx > 0 ? Math.min(1, Math.abs(dx) / 45) : 0;
              bgRight.style.opacity = dx < 0 ? Math.min(1, Math.abs(dx) / 45) : 0;
            }
          }
        };

        const onEnd = async () => {
          cancelPress();
          if (!isSwiping) return;
          isSwiping = false;
          row.style.transition = 'transform 0.2s cubic-bezier(0.25, 1, 0.5, 1)';
          row.style.transform = 'translateX(0px)';
          if (bgLeft) bgLeft.style.opacity = '0';
          if (bgRight) bgRight.style.opacity = '0';

          if (isHorizontal) {
            if (currentDx > 60) {
              // 👉 Swipe Right: Toggle Check / Uncheck (100% Quick Buy)
              lastLocalMutationTime = Date.now();
              const willCheck = !(item.isChecked || item.is_checked);
              const actualQty = willCheck ? (item.quantity || item.quantidade || 1) : 0;
              const actualPrice = willCheck ? (item.actualPrice || item.precoReal || 0) : 0;
              const itemCat = item.categoryName || item.categoriaNome || 'Outros';
              if (willCheck) {
                expandedComprados.add(itemCat);
              }

              const updatedPayload = {
                ...item,
                isChecked: willCheck,
                actualQuantity: actualQty,
                actualPrice,
              };

              const idx = (lista.items || []).findIndex(i => i.id === item.id);
              if (idx > -1) lista.items[idx] = updatedPayload;

              if (authManager.isAuthenticated()) {
                authManager.fetchWithAuth(`/api/listas/${lista.id}/itens/${item.id}`, {
                  method: 'PUT',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify(updatedPayload),
                }).catch(() => {});
              }
              await toggleListaItemLocal(item.id, lista.id, willCheck, actualQty, actualPrice);
              if (syncManager?.showToast) {
                syncManager.showToast(willCheck ? 'Item marcado como comprado!' : 'Item desmarcado.', willCheck ? 'success' : 'info');
              }
              render();
            } else if (currentDx < -60) {
              // 👈 Swipe Left: Open Modal for Partial Qty / Price
              triggerModal();
            }
          }
        };

        // Touch events (passive)
        row.addEventListener('touchstart', (e) => {
          const touch = e.touches[0];
          if (touch) onStart(touch.clientX, touch.clientY, e.target);
        }, { passive: true });

        row.addEventListener('touchmove', (e) => {
          const touch = e.touches[0];
          if (touch) onMove(touch.clientX, touch.clientY);
        }, { passive: true });

        row.addEventListener('touchend', onEnd, { passive: true });
        row.addEventListener('touchcancel', onEnd, { passive: true });

        // Mouse drag events (Desktop)
        row.addEventListener('mousedown', (e) => {
          onStart(e.clientX, e.clientY, e.target);
        });
        window.addEventListener('mousemove', (e) => {
          if (isSwiping) onMove(e.clientX, e.clientY);
        });
        window.addEventListener('mouseup', () => {
          if (isSwiping) onEnd();
        });

        // Prevent native context menu on long press
        row.addEventListener('contextmenu', (e) => {
          e.preventDefault();
          e.stopPropagation();
        });

        // 2 Cliques / Double Click / Double Tap
        row.addEventListener('click', (e) => {
          if (currentIsClosed || isLongPressed || isHorizontal) return;
          if (e.target.closest('.item-checkbox') || e.target.closest('.btn-delete-item') || e.target.closest('.btn-tag-item') || e.target.closest('.btn-item-qty-chip')) {
            return;
          }
          const now = Date.now();
          if (now - lastClickTime < 380 && now - lastClickTime > 0) {
            cancelPress();
            triggerModal();
            lastClickTime = 0;
          } else {
            lastClickTime = now;
          }
        });

        row.addEventListener('dblclick', (e) => {
          if (currentIsClosed || isHorizontal) return;
          if (e.target.closest('.item-checkbox') || e.target.closest('.btn-delete-item') || e.target.closest('.btn-tag-item') || e.target.closest('.btn-item-qty-chip')) {
            return;
          }
          cancelPress();
          triggerModal();
        });
      });

      // Click on Checkbox -> Opens Confirmation Modal (Prevents accidental toggling)
      container.querySelectorAll('.item-checkbox').forEach(chk => {
        chk.onclick = (e) => {
          e.preventDefault();
          e.stopPropagation();
          if (currentIsClosed) return;
          const rawId = chk.getAttribute('data-item-id');
          const item = currentItems.find(it => String(it.id) === String(rawId));
          if (item) openBuyModal(item);
        };
      });

      // Delete item buttons
      container.querySelectorAll('.btn-delete-item').forEach(btn => {
        btn.onclick = (e) => {
          e.stopPropagation();
          if (currentIsClosed) return;
          const itemId = Number(btn.getAttribute('data-item-id'));
          const item = currentItems.find(it => it.id === itemId);
          if (item) openDeleteItemModal(item);
        };
      });
    }

    // Bind item row events for initial render
    bindItemRowEvents();

    // Send chat message
    const chatForm = container.querySelector('#form-send-chat-msg');
    if (chatForm) {
      chatForm.addEventListener('submit', async (e) => {
        e.preventDefault();
        const msgInput = container.querySelector('#input-chat-msg');
        const text = msgInput?.value.trim();
        if (!text) return;

        let fullMessage = text;
        if (quotedItem) {
          fullMessage = `[🏷️ ${quotedItem.description}] ${fullMessage}`;
        }
        if (replyToMessage) {
          const snippet = replyToMessage.text.length > 35 ? replyToMessage.text.slice(0, 35) + '...' : replyToMessage.text;
          fullMessage = `[↩️ ${replyToMessage.userName}: "${snippet}"] ${fullMessage}`;
        }

        const user = authManager.getUser();
        const msgPayload = {
          listId: lista.id,
          userId: user?.id || 'usr_local',
          userName: user?.name || user?.email?.split('@')[0] || 'Usuário',
          userPicture: user?.picture || '',
          message: fullMessage,
        };

        if (authManager.isAuthenticated()) {
          try {
            const res = await authManager.fetchWithAuth(`/api/listas/${lista.id}/mensagens`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(msgPayload),
            });
            if (res.ok) {
              const created = await res.json();
              await saveListaMensagemLocal(created);
            } else {
              await saveListaMensagemLocal(msgPayload);
            }
          } catch (err) {
            await saveListaMensagemLocal(msgPayload);
          }
        } else {
          await saveListaMensagemLocal(msgPayload);
        }

        quotedItem = null;
        replyToMessage = null;
        msgInput.value = '';
        if (lista && Array.isArray(lista.messages)) {
          lista.messages.push(msgPayload);
        }
        render();
        const stream = container.querySelector('#chat-messages-stream');
        if (stream) stream.scrollTop = stream.scrollHeight;
      });
    }
  }

  render();

  // Polling silencioso do chat e itens a cada 10 segundos
  const chatPollInterval = setInterval(async () => {
    // Se o usuário saiu da tela da lista, encerra o polling
    if (!document.body.contains(container) || window.location.hash.indexOf('lista') === -1) {
      clearInterval(chatPollInterval);
      return;
    }

    // Só realiza polling se a lista estiver aberta (não fechada), a aba estiver visível e autenticado
    if (!lista || lista.status === 'fechada' || document.hidden || !authManager.isAuthenticated()) {
      return;
    }

    try {
      const res = await authManager.fetchWithAuth(`/api/listas/${id}`);
      if (res.ok) {
        const freshData = await res.json();
        const oldMsgCount = (lista.messages || []).length;
        const newMsgCount = (freshData.messages || []).length;
        const oldItemsCount = (lista.items || []).length;
        const newItemsCount = (freshData.items || []).length;
        
        // Verifica se houve mudanças em mensagens ou itens da lista
        const hasMsgChanges = oldMsgCount !== newMsgCount;
        
        // Se houve mutação recente do usuário nos últimos 4 segundos, ignora atualizações de itens do polling
        const isRecentUserMutation = (Date.now() - lastLocalMutationTime) < 4000;
        const hasItemChanges = !isRecentUserMutation && (oldItemsCount !== newItemsCount || 
          JSON.stringify(lista.items?.map(i => ({ id: i.id, checked: i.isChecked || i.is_checked, q: i.quantity }))) !==
          JSON.stringify(freshData.items?.map(i => ({ id: i.id, checked: i.isChecked || i.is_checked, q: i.quantity }))));

        if (hasMsgChanges && !hasItemChanges) {
          lista.messages = freshData.messages || [];
          render();
        } else if (hasItemChanges) {
          const activeInput = document.activeElement;
          const inputChat = container.querySelector('#input-chat-msg');
          const isTypingInChat = activeInput && activeInput === inputChat;
          const currentChatText = inputChat ? inputChat.value : '';

          lista = freshData;
          render();

          // Preserva o texto digitado caso o usuário esteja escrevendo no chat durante a atualização
          if (isTypingInChat) {
            const newChatInput = container.querySelector('#input-chat-msg');
            if (newChatInput) {
              newChatInput.value = currentChatText;
              newChatInput.focus();
            }
          }

          // Se o chat estiver aberto e chegaram novas mensagens, rola para a mais recente
          if (!isChatCollapsed && hasMsgChanges) {
            const stream = container.querySelector('#chat-messages-stream');
            if (stream) stream.scrollTop = stream.scrollHeight;
          }
        }
      }
    } catch (e) {
      // Falha silenciosa de rede/conexão sem interromper a navegação
    }
  }, 10000);
}
